class GlobalUploadModal {
  constructor() {
    this.init();
    this.activeFileInput = null;
    this.currentFile = null;
    this.originalFile = null; // Store original file for regeneration
    this.confirmCallback = null;
    this.regenerateCallback = null; // Callback for regeneration
    this.selectedImageUrl = null;
    this.selectedDisplayUrl = null; // Display-only image (fileKeyImg1)
    this.selectedImageModelName = null;
    this.abortController = null; // For canceling requests
    this.progressAnimation = null; // For stopping progress animation
    this.carouselInterval = null; // For carousel auto-play
    this.currentCarouselIndex = 0; // Current carousel index
    this.isGenerateSubmitting = false; // Prevent duplicate generate submissions
    this.closeResetTimer = null;
    this.cropScale = 1;
    this.cropBaseScale = 1;
    this.cropOffsetX = 0;
    this.cropOffsetY = 0;
    this.cropDragging = false;
    this.cropDragStart = null;
    this.cropPointers = new Map();
    this.cropPinchStart = null;

    // State tracking for analytics
    this.currentState = "preview"; // 'preview', 'loading', 'selection'
    this.generationStartTime = null; // Track when generation started
    this.generatedImageCount = 0; // Track number of generated images
    this.currentSourceType = null; // Track current generation source type for model_name

    // Regeneration limit tracking
    this.regenerateCount = 0; // Track number of regeneration attempts
    this.maxRegenerateCount = 5; // Maximum allowed regeneration attempts per modal session

    // History selection tracking
    this.isFromHistory = false; // Track if selection is from history
    this.selectedHistoryUrl = null; // Track selected history image URL
    this.selectedHistoryDisplayUrl = null; // Track selected history display URL
    this.selectedHistoryJobId = null; // Track selected history job ID
    this.selectedHistoryModelName = null;
    this.openedDirectlyToHistory = false; // Track if opened directly to history view

    // History limit tracking
    this.maxHistoryItems = 30; // Maximum number of history items to load
    this.historyItemCount = 0; // Track number of loaded history items

    // Style mapping (same as image-uploader.js)
    this.styleMapping = {
      Chibi: "Q-Style",
      Pixar: "Pixar-Style",
      Minecraft: "Minecraft-Style",
      Funko: "Funko-Style",
      Realistic: "Realistic-Style",
      "Line Healing Style":"Line-Healing-Style",
      "3D Oil Painting":"3D-Embossed-Lightbox",
      "3D Oil Relief - Natural Wood":"3D-Embossed-Lightbox",
      "3D Oil Relief - Walnut":"3D-Embossed-Lightbox",
      "Oil Painting":"oil_painting"
    };

    // Face detection
    this.faceApiLoaded = false; // Track if face-api.js is loaded
    this.faceApiLoading = false; // Track if face-api.js is currently loading
    this.detectedFaceCount = 0; // Track number of detected faces

    // Bind the beforeunload handler so we can remove it later
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
  }

  /**
   * Get current model name based on sourceType
   * @returns {string} model name (seedream or chatgpt_4o)
   */
  getCurrentModelName() {
    if (typeof GtagUtils !== "undefined" && this.currentSourceType) {
      return GtagUtils.getModelName(this.currentSourceType);
    }
    return "chatgpt_4o"; // Default
  }

  getImageModelName(index) {
    if (this.currentSourceType === "cute_single_img2img_deliver3d_long") {
      return index === 0 ? "seedream" : "chatgpt_4o";
    }

    return this.getCurrentModelName();
  }

  /**
   * Handle page unload during generation (for analytics)
   */
  handleBeforeUnload() {
    if (this.currentState === "loading" && typeof GtagUtils !== "undefined") {
      const stayDuration = this.generationStartTime
        ? Math.round((Date.now() - this.generationStartTime) / 1000)
        : 0;
      const modelName = this.getCurrentModelName();
      GtagUtils.trackGenerationAbandoned(
        stayDuration,
        this.generatedImageCount,
        modelName
      );
    }
  }

  /**
   * Load face-api.js library dynamically
   * @returns {Promise<void>}
   */
  async loadFaceApi() {
    if (this.faceApiLoaded) {
      return;
    }

    if (this.faceApiLoading) {
      // Wait for existing load to complete
      while (this.faceApiLoading) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      return;
    }

    this.faceApiLoading = true;

    try {
      // Load face-api.js from local assets
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        // Use Shopify asset URL or fallback to CDN
        const assetUrl =
          window.faceApiAssetUrl ||
          "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js";
        script.src = assetUrl;
        script.onload = resolve;
        script.onerror = () => reject(new Error("Failed to load face-api.js"));
        document.head.appendChild(script);
      });

      // Load the face detection model
      // If local model URLs are provided, load from Shopify assets
      // Otherwise fallback to CDN
      if (window.faceApiModelManifestUrl && window.faceApiModelBinUrl) {
        // Load model from local Shopify assets
        // Fetch the manifest
        const manifestResponse = await fetch(window.faceApiModelManifestUrl);
        const manifest = await manifestResponse.json();

        // Fetch the binary weights
        const weightsResponse = await fetch(window.faceApiModelBinUrl);
        const weightsBuffer = await weightsResponse.arrayBuffer();

        // Parse weights according to manifest
        const weightSpecs = manifest[0].weights;
        const weightData = new Uint8Array(weightsBuffer);

        // Create weight map from the binary data
        const weightMap = {};
        let offset = 0;

        for (const spec of weightSpecs) {
          const { name, shape, dtype, quantization } = spec;
          const size = shape.reduce((a, b) => a * b, 1);

          // Extract quantized data
          const quantizedData = weightData.slice(offset, offset + size);
          offset += size;

          // Dequantize the data
          const floatData = new Float32Array(size);
          for (let i = 0; i < size; i++) {
            floatData[i] =
              quantizedData[i] * quantization.scale + quantization.min;
          }

          // Create tensor
          weightMap[name] = faceapi.tf.tensor(floatData, shape, "float32");
        }

        // Load weights into the model
        await faceapi.nets.tinyFaceDetector.loadFromWeightMap(weightMap);
      } else {
        // Fallback to CDN
        const modelUrl =
          "https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/model";
        await faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl);
      }

      this.faceApiLoaded = true;
    } catch (error) {
      throw error;
    } finally {
      this.faceApiLoading = false;
    }
  }

  /**
   * Detect faces in an image file
   * @param {File} file - Image file to analyze
   * @returns {Promise<number>} Number of faces detected
   */
  async detectFaces(file) {
    try {
      // Ensure face-api.js is loaded
      await this.loadFaceApi();

      // Create an image element from the file
      const img = await this.createImageFromFile(file);

      // Detect faces using TinyFaceDetector (faster than SSD)
      const detections = await faceapi.detectAllFaces(
        img,
        new faceapi.TinyFaceDetectorOptions({
          inputSize: 416,
          scoreThreshold: 0.4,
        })
      );

      this.detectedFaceCount = detections.length;

      return this.detectedFaceCount;
    } catch (error) {
      // Return 0 on error to allow generation to proceed
      return 0;
    }
  }

  /**
   * Create an HTMLImageElement from a File
   * @param {File} file - Image file
   * @returns {Promise<HTMLImageElement>}
   */
  createImageFromFile(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Failed to load image for face detection"));
      };

      img.src = objectUrl;
    });
  }

  /**
   * Check if current selection is for single person
   * @returns {boolean}
   */
  isSinglePersonSelected() {
    // Check if characterType is "person" (single person)
    // "couple" and "human-pet" are multi-person types
    return this.characterType === "person";
  }

  /**
   * Show multi-face warning dialog and close the upload modal
   * @param {number} faceCount - Number of faces detected
   */
  showMultiFaceWarning(faceCount) {
    // Create warning modal
    const warningModal = document.createElement("div");
    warningModal.className = "face-warning-modal-overlay";
    warningModal.innerHTML = `
      <div class="face-warning-modal">
        <div class="face-warning-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#f59e0b" stroke-width="2" fill="none"/>
            <path d="M12 8V12M12 16H12.01" stroke="#f59e0b" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h3 class="face-warning-title">Multiple Faces Detected</h3>
        <p class="face-warning-suggestion">
          Please select the <strong>"2 People (Together)"</strong> option to generate with multiple people.
        </p>
        <div class="face-warning-buttons">
          <button type="button" class="face-warning-btn-ok">OK</button>
        </div>
      </div>
    `;

    document.body.appendChild(warningModal);

    // Close both warning and upload modal when OK is clicked
    const okBtn = warningModal.querySelector(".face-warning-btn-ok");

    const closeAll = () => {
      warningModal.remove();
      this.close(); // Close the main upload modal
    };

    okBtn.addEventListener("click", closeAll);

    // Also close on overlay click
    warningModal.addEventListener("click", (e) => {
      if (e.target === warningModal) {
        closeAll();
      }
    });
  }

  /**
   * Show no face detected warning dialog
   */
  showNoFaceWarning() {
    // Create warning modal
    const warningModal = document.createElement("div");
    warningModal.className = "face-warning-modal-overlay";
    warningModal.innerHTML = `
      <div class="face-warning-modal">
        <div class="face-warning-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#f59e0b" stroke-width="2" fill="none"/>
            <path d="M12 8V12M12 16H12.01" stroke="#f59e0b" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h3 class="face-warning-title">No Face Detected</h3>
        <p class="face-warning-message">
          We couldn't detect a face in your photo.
        </p>
        <p class="face-warning-suggestion">
          Please upload a clear photo with a visible face for best results.
        </p>
        <div class="face-warning-buttons">
          <button type="button" class="face-warning-btn-ok">Change Photo</button>
        </div>
      </div>
    `;

    document.body.appendChild(warningModal);

    // Close warning modal when button is clicked (don't close upload modal)
    const okBtn = warningModal.querySelector(".face-warning-btn-ok");

    const closeAll = () => {
      warningModal.remove();
      this.close(); // Close the main upload modal
    };

    okBtn.addEventListener("click", closeAll);

    // Also close on overlay click
    warningModal.addEventListener("click", (e) => {
      if (e.target === warningModal) {
        closeAll();
      }
    });
  }

  init() {
    // If modal already exists, don't create it again
    if (document.getElementById("globalUploadModal")) {
      this.bindElements();
      return;
    }

    // Create and insert modal HTML
    const modalHTML = this.createModalHTML();
    document.body.insertAdjacentHTML("beforeend", modalHTML);
    this.bindElements();
  }

  getGenerateButtonText() {
    const configuredText = String(
      window.THEME_CONFIG?.generateButtonText || ""
    ).trim();
    return configuredText || "Generate Preview";
  }

  bindElements() {
    this.modal = document.getElementById("globalUploadModal");
    this.previewImage = document.getElementById("uploadPreviewImage");
    this.previewFile = document.getElementById("uploadPreviewFile");
    this.closeButton = this.modal.querySelector(".global-upload-modal-close");
    // Remove the old button when reusing a modal left by a theme hot reload.
    this.modal.querySelector(".global-upload-modal-quick")?.remove();
    this.betterButton = this.modal.querySelector(".global-upload-modal-better");
    const betterName = this.betterButton?.querySelector(".btn-name");
    if (betterName) {
      betterName.textContent = this.getGenerateButtonText();
    }
    this.confirmButton = this.modal.querySelector(
      ".global-upload-modal-confirm"
    );
    this.buttonsContainer = this.modal.querySelector(
      ".global-upload-modal-buttons"
    );
    this.regenerateButton = this.modal.querySelector(
      ".global-upload-modal-regenerate"
    );
    this.cropState = this.modal.querySelector("#uploadCropState");
    this.cropStage = this.modal.querySelector("#uploadCropStage");
    this.cropImage = this.modal.querySelector("#uploadCropImage");
    this.cropClearImage = this.modal.querySelector("#uploadCropClearImage");
    // A theme hot reload can leave an older modal DOM in place. Rehydrate the
    // crop image node so the current controller can still use that modal.
    if (this.cropState && !this.cropStage) {
      this.cropStage = document.createElement("div");
      this.cropStage.id = "uploadCropStage";
      this.cropStage.className = "upload-crop-stage";
      this.cropState.prepend(this.cropStage);
    }
    if (this.cropStage && !this.cropImage) {
      this.cropImage = document.createElement("img");
      this.cropImage.id = "uploadCropImage";
      this.cropImage.alt = "Crop preview";
      this.cropImage.draggable = false;
      this.cropStage.prepend(this.cropImage);
    }
    if (this.cropStage && !this.cropStage.querySelector(".upload-crop-frame")) {
      const frame = document.createElement("div");
      frame.className = "upload-crop-frame";
      frame.setAttribute("aria-hidden", "true");
      this.cropStage.appendChild(frame);
    }
    this.cropZoom = this.modal.querySelector("#uploadCropZoom");
    this.cropResetButton = this.modal.querySelector("#uploadCropReset");
    this.cropConfirmButton = this.modal.querySelector("#uploadCropConfirm");
    this.cropZoomOutButton = this.modal.querySelector("#uploadCropZoomOut");
    this.cropZoomInButton = this.modal.querySelector("#uploadCropZoomIn");
    this.cropFrame = this.modal.querySelector(".upload-crop-frame");

    // Recent Result elements
    this.btnRecentResult = this.modal.querySelector("#btnRecentResult");
    this.historyState = this.modal.querySelector("#uploadHistoryState");
    this.historyGrid = this.modal.querySelector("#historyImagesGrid");
    this.btnBackToPreview = this.modal.querySelector("#btnBackToPreview");
    this.btnConfirmHistory = this.modal.querySelector("#btnConfirmHistory");

    this.setupEventListeners();
  }

  /**
   * 生成轮播 HTML
   */
  generateCarouselHTML() {
    // 检查全局变量是否存在
    if (
      !window.CAROUSEL_IMAGES ||
      !window.CAROUSEL_IMAGES.original ||
      !window.CAROUSEL_IMAGES.generated
    ) {
      return '<div class="carousel-placeholder">Loading images...</div>';
    }

    const { original, generated } = window.CAROUSEL_IMAGES;
    const groupCount = Math.min(original.length, generated.length);

    return Array.from(
      { length: groupCount },
      (_, index) => `
      <div class="carousel-group ${
        index === 0 ? "active" : ""
      }" data-carousel-index="${index}">
        <div class="image-card animate-slide-in-left">
          <img src="${original[index]}" alt="Original">
        </div>
        <div class="image-card animate-slide-in-right">
          <img src="${generated[index]}" alt="Generated 3D">
        </div>
      </div>
    `
    ).join("");
  }

  createModalHTML() {
    const cropAssets = window.CROP_ASSETS || {};
    return `
      <div id="globalUploadModal" class="global-upload-modal-overlay">
        <div class="global-upload-modal">
          <div class="global-upload-modal-header">
            <div class="global-upload-modal-title">Preview Upload File</div>
            <!-- 多人提示 - 在标题旁边显示 -->
            <div class="upload-info-notice" style="display: none;">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="9" stroke="#3b82f6" stroke-width="1.5" fill="none"/>
                <path d="M10 6V10M10 14H10.01" stroke="#3b82f6" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <span>Order now and get the <b style="color:#e53e3e">Crystal Base</b>, <b style="color:#e53e3e">Printed Photo</b> and <b style="color:#e53e3e">Luxury Gift Box</b> shown in the picture <b style="color:#e53e3e">for free</b>!</span>
            </div>
            <button type="button" class="global-upload-modal-close" aria-label="Close"><img src="${cropAssets.close || ""}" alt=""></button>
          </div>
          <div class="global-upload-modal-content">
            <!-- 预览状态 -->
            <div id="uploadPreviewState" class="global-upload-preview">
              <img id="uploadPreviewImage" class="upload-preview-image" src="" alt="Preview Image">
              <div id="uploadPreviewFile" class="upload-preview-file">
                <div class="file-icon">📄</div>
                <div class="file-info">
                  <div class="file-name"></div>
                  <div class="file-size"></div>
                </div>
              </div>
            </div>
            <div id="uploadCropState" class="upload-crop-state" style="display: none;">
              <div class="upload-crop-stage" id="uploadCropStage">
                <img id="uploadCropImage" alt="Crop preview" draggable="false">
                <div class="upload-crop-backdrop"></div>
                <img id="uploadCropClearImage" class="upload-crop-clear-image" alt="" draggable="false">
                <div class="upload-crop-frame" aria-hidden="true"></div>
              </div>
              <div class="upload-crop-controls">
                <div class="upload-crop-zoom">
                  <button type="button" id="uploadCropZoomOut" class="upload-crop-zoom-button" aria-label="Zoom out"><img src="${cropAssets.minus || ""}" alt=""></button>
                  <input id="uploadCropZoom" type="text" inputmode="decimal" value="100%" aria-label="Image zoom percentage">
                  <button type="button" id="uploadCropZoomIn" class="upload-crop-zoom-button" aria-label="Zoom in"><img src="${cropAssets.plus || ""}" alt=""></button>
                </div>
              </div>
              <p class="upload-crop-help"><img src="${cropAssets.sparkle || ""}" alt="">Drag to reposition and use the controls to zoom. Anything outside the frame will be cropped.</p>
              <div class="upload-crop-actions">
                <button type="button" id="uploadCropReset" class="upload-crop-reset">Reset</button>
                <button type="button" id="uploadCropConfirm" class="upload-crop-confirm">Confirm</button>
              </div>
            </div>
            <div id="uploadHistoryState" class="upload-history-state" style="display: none;">
                <div class="history-grid" id="historyImagesGrid" style="max-height: 300px; overflow-y: auto;">
                    <!-- 历史图片动态插入 -->
                </div>
                <div style="text-align: center; margin-top: 15px; display: flex; justify-content: flex-end; gap: 10px;">
                    <button type="button" id="btnBackToPreview" class="global-upload-modal-back" style="background: #f0f0f0; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">Back</button>
                    <button type="button" class="global-upload-modal-history-confirm" id="btnConfirmHistory" disabled >Confirm Selection</button>
                </div>
            </div>

            <!-- 进度条状态 -->
            <div id="uploadProgressState" class="upload-progress-state" style="display: none;">
              <!-- 轮播动画 -->
              <div class="carousel-container">
                ${this.generateCarouselHTML()}
              </div>

              <h3 class="progress-title">Please wait a moment...</h3>
              <div class="progress-bar-container">
                <div class="progress-bar" id="uploadProgressBar"></div>
              </div>
              <div class="progress-text" id="uploadProgressText"></div>
            </div>

            <!-- 图片选择状态 -->
            <div id="imageSelectionState" class="image-selection-state" style="display: none;">
              <!-- 大预览图 -->
              <div class="image-preview-container">
                <div class="image-preview-main" id="image-preview-main">
                  <div class="image-loading-spinner"></div>
                </div>
                <!-- Recent Result button - bottom right of preview -->
                <div class="recent-container">
                  <button type="button" id="btnRecentResult" class="global-upload-modal-recent">
                    <svg width="35" height="35" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 8V12L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M3.05 11C3.27 6.06 7.26 2.14 12.2 2.01C17.41 1.87 21.77 6.01 21.99 11.21C22.22 16.55 18.06 21 12.75 21H12C7.59 21 4 17.41 4 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M1 9L3 11L5 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
              <!-- 缩略图列表 -->
              <div class="select-image-list"><div class="image-thumbnails" id="select-image-list">
                <!-- 动态插入缩略图 -->
              </div></div>
              <!-- 特殊风格提示文案 -->
              <div id="special-style-notice" style="display: none; margin-top:10px; font-size: 12px; line-height: 1.3;color: #6b7280;">
              </div>
            </div>
          </div>
          <div class="global-upload-modal-buttons">
            <button type="button" class="global-upload-modal-regenerate" style="display: none;">Regenerate</button>
            <button type="button" class="global-upload-modal-generate global-upload-modal-better" data-source-type="cute_single_img2img_deliver3d_long">
              <span class="btn-name-row">
                <svg class="btn-icon" xmlns="http://www.w3.org/2000/svg" fill="none" width="20" height="20" viewBox="0 0 19.548009872436523 19.518518447875977"><path d="M9.7740049,19.518518C9.5728226,19.518518,9.380023,19.485039,9.1872225,19.40971C8.7261801,19.242311,8.3657274,18.882404,8.1980753,18.42206L6.3036075,13.224346L1.0980116,11.332747C0.67049897,11.173719,0.33519483,10.864032,0.15077758,10.453906C-0.033639658,10.043781,-0.050404929,9.5918055,0.10886456,9.1733103C0.27651662,8.712966,0.63696855,8.3530588,1.0980116,8.1856613L6.3036075,6.2940617L8.1980753,1.0963489C8.3573446,0.6694836,8.6675014,0.3346872,9.078249,0.15054926C9.4889956,-0.033588719,9.9416571,-0.050328601,10.360787,0.10869971C10.82183,0.27609786,11.182282,0.63600397,11.349935,1.0963489L13.244404,6.2940617L18.449999,8.1856613C18.877512,8.3446894,19.212816,8.6543751,19.397232,9.0645008C19.58165,9.4746265,19.598413,9.9266024,19.439146,10.345098C19.271494,10.805443,18.911041,11.165348,18.449999,11.332747L13.244401,13.224346L11.349933,18.42206C11.190664,18.848925,10.880507,19.183722,10.46976,19.367859C10.251811,19.4683,10.0171,19.518518,9.7740049,19.518518ZM2.6655583,9.7675753L7.1418686,11.391337C7.602911,11.558736,7.9801283,11.935381,8.1477804,12.395726L9.7740049,16.865257L11.400229,12.395726C11.567882,11.935381,11.945098,11.558736,12.406141,11.391337L16.88245,9.7675743L12.406141,8.1438131C11.945098,7.9764142,11.567882,7.5997686,11.400229,7.1394234L9.7740049,2.6698935L8.1477795,7.1394243C7.9801278,7.5997696,7.6029105,7.9764161,7.1418681,8.1438141L2.6655583,9.7675753Z" fill="currentColor"/></svg>
                <span class="btn-name">Generate Preview</span>
              </span>
              <span class="btn-time">About 35 seconds</span>
            </button>
            <button type="button" class="global-upload-modal-confirm" style="display: none;">Confirm Selection</button>
          </div>
        </div>
      </div>
    `;
  }

  updateUploadInfoNoticeText(notice) {
    if (!notice) return;

    const text = notice.querySelector("span");
    if (!text) return;

    if (this.detectIsAromaStone()) {
      const whatsappLink = this.getWhatsAppLink();
      text.innerHTML = `
        Need multiple pets? Contact us via
        <a href="mailto:support@snapfigures.com" style="color:#111827;font-weight:600;text-decoration:underline;text-underline-offset:2px;">Email</a>
        or
        <a href="${whatsappLink}" target="_blank" rel="noopener noreferrer" style="color:#25D366;font-weight:600;text-decoration:underline;text-underline-offset:2px;">WhatsApp</a>.
      `;
      return;
    }

    text.innerHTML =
      'Order now and get the <b style="color:#e53e3e">Crystal Base</b>, <b style="color:#e53e3e">Printed Photo</b> and <b style="color:#e53e3e">Luxury Gift Box</b> shown in the picture <b style="color:#e53e3e">for free</b>!';
  }

  setupEventListeners() {
    // Listen for all file input changes (backward compatibility)
    document.addEventListener("change", (e) => {
      if (e.target.matches(".custom-file-input")) {
        this.handleFileSelect(e);
      }
    });

    // Close button
    this.closeButton.addEventListener("click", (e) => {
      e.stopPropagation();
      this.close();
    });

    // Track generate click throttle (500ms)
    let lastGenerateTrackTime = 0;

    // Better Generate button
    this.betterButton.addEventListener(
      "click",
      (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();

        // Track Better Generate click (500ms throttle)
        const now = Date.now();
        if (typeof GtagUtils !== "undefined" && now - lastGenerateTrackTime > 500) {
          lastGenerateTrackTime = now;
          GtagUtils.trackBetterGenerateClick('gpt-4o');
        }

        const sourceType = this.betterButton.dataset.sourceType;
        setTimeout(() => {
          this.confirm(sourceType);
        }, 0);
      },
      true
    );

    // Recent Result button
    if (this.btnRecentResult) {
      this.btnRecentResult.addEventListener("click", (e) => {
        e.stopPropagation();
        this.showHistory();
      });
    }

    // Back from History - return to image selection state
    if (this.btnBackToPreview) {
      this.btnBackToPreview.addEventListener("click", (e) => {
        e.stopPropagation();
        this.backFromHistory();
      });
    }

    // Confirm History Selection button
    if (this.btnConfirmHistory) {
      this.btnConfirmHistory.addEventListener("click", (e) => {
        e.stopPropagation();
        this.confirmHistorySelection();
      });
    }

    // Confirm Selection button (for image selection state)
    this.confirmButton.addEventListener(
      "click",
      (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();

        setTimeout(() => {
          this.confirm();
        }, 0);
      },
      true
    );

    // Regenerate button
    this.regenerateButton.addEventListener(
      "click",
      (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();

        this.regenerate();
      },
      true
    );

    if (this.cropZoom) {
      const applyZoomInput = () => {
        const percentage = Number.parseFloat(this.cropZoom.value.replace("%", ""));
        if (!Number.isFinite(percentage)) {
          this.updateCropZoomInput();
          return;
        }
        const focalPoint = this.getCropFrameCenter();
        this.setCropScale(percentage / 100, focalPoint.x, focalPoint.y);
      };
      this.cropZoom.addEventListener("change", applyZoomInput);
      this.cropZoom.addEventListener("blur", applyZoomInput);
      this.cropZoom.addEventListener("keydown", (event) => {
        if (event.key === "Enter") this.cropZoom.blur();
      });
    }
    if (this.cropZoomOutButton) this.cropZoomOutButton.addEventListener("click", () => this.stepCropZoom(-0.1));
    if (this.cropZoomInButton) this.cropZoomInButton.addEventListener("click", () => this.stepCropZoom(0.1));
    if (this.cropResetButton) this.cropResetButton.addEventListener("click", () => this.resetCrop());
    if (this.cropConfirmButton) this.cropConfirmButton.addEventListener("click", () => this.confirmCrop());
    if (this.cropStage) {
      this.cropStage.addEventListener("dragstart", (event) => event.preventDefault());
      this.cropStage.addEventListener("pointerdown", (event) => {
        if (!this.cropImage?.src) return;
        const point = this.getCropStagePoint(event.clientX, event.clientY);
        this.cropPointers.set(event.pointerId, point);
        this.cropStage.setPointerCapture?.(event.pointerId);
        if (this.cropPointers.size === 1) {
          this.cropDragging = true;
          this.cropDragStart = { x: point.x, y: point.y, left: this.cropOffsetX, top: this.cropOffsetY };
        } else if (this.cropPointers.size === 2) {
          this.cropDragging = false;
          this.startCropPinch();
        }
      });
      this.cropStage.addEventListener("pointermove", (event) => {
        if (!this.cropPointers.has(event.pointerId)) return;
        const point = this.getCropStagePoint(event.clientX, event.clientY);
        this.cropPointers.set(event.pointerId, point);
        if (this.cropPointers.size >= 2 && this.cropPinchStart) {
          this.updateCropPinch();
        } else if (this.cropDragging && this.cropDragStart) {
          this.cropOffsetX = this.cropDragStart.left + point.x - this.cropDragStart.x;
          this.cropOffsetY = this.cropDragStart.top + point.y - this.cropDragStart.y;
          this.renderCropImage();
        }
      });
      ["pointerup", "pointercancel", "lostpointercapture"].forEach((type) => this.cropStage.addEventListener(type, (event) => {
        this.cropPointers.delete(event.pointerId);
        this.cropPinchStart = null;
        const remainingPoint = this.cropPointers.values().next().value;
        if (remainingPoint) {
          this.cropDragging = true;
          this.cropDragStart = { x: remainingPoint.x, y: remainingPoint.y, left: this.cropOffsetX, top: this.cropOffsetY };
        } else {
          this.cropDragging = false;
          this.cropDragStart = null;
        }
      }));
      this.cropStage.addEventListener("wheel", (event) => {
        if (!this.cropImage?.naturalWidth) return;
        event.preventDefault();
        const point = this.getCropStagePoint(event.clientX, event.clientY);
        const nextScale = this.cropScale * Math.exp(-event.deltaY * 0.002);
        this.setCropScale(nextScale, point.x, point.y);
      }, { passive: false });
    }

    // Close when clicking overlay
    // this.modal.addEventListener('click', (e) => {
    //   e.stopPropagation(); // Prevent event bubbling to drawer and popover
    //   if (e.target === this.modal) {
    //     this.close();
    //   }
    // });
  }

  handleFileSelect(event) {
    const file = event.target.files[0];
    if (!file) return;

    this.activeFileInput = event.target;
    this.currentFile = file;

    // Check if file is an image - by MIME type or file extension
    const fileName = file.name.toLowerCase();
    const isImage =
      file.type.startsWith("image/") ||
      fileName.endsWith(".jpg") ||
      fileName.endsWith(".jpeg") ||
      fileName.endsWith(".png") ||
      fileName.endsWith(".webp") ||
      fileName.endsWith(".heic") ||
      fileName.endsWith(".gif");

    if (isImage) {
      this.showImagePreview(file);
    } else {
      this.showFilePreview(file);
    }

    this.open();
    const shouldCrop = isImage && this.shouldEnableImageCrop();
    if (shouldCrop) this.showCropState(file);
  }

  /**
   * 显示弹窗并预览文件（新的公共 API）
   * @param {string} [characterType] - 角色类型 (pet, person, couple, etc.)
   * @param {Function} onConfirm - 用户点击确认后的回调函数，接收文件作为参数
   * @param {Function} [onRegenerate] - 可选，用户点击重新生成后的回调函数
   */
  show(file, characterType, onConfirm, onRegenerate) {
    if (!file) {
      return;
    }

    // Handle variable arguments: if characterType is function, shift arguments
    if (typeof characterType === "function") {
      onRegenerate = onConfirm;
      onConfirm = characterType;
      characterType = "person"; // Default
    }

    this.currentFile = file;
    this.originalFile = file; // Store original file for regeneration
    this.characterType = characterType; // Store character type
    this.confirmCallback = onConfirm;
    this.regenerateCallback = onRegenerate || onConfirm; // Use onConfirm as fallback

    // Clear history selection state when starting new generation
    this.isFromHistory = false;
    this.selectedHistoryUrl = null;
    this.selectedHistoryDisplayUrl = null;
    this.selectedHistoryModelName = null;
    this.openedDirectlyToHistory = false;

    // Clear selected image URLs to prevent old values from being used
    this.selectedImageUrl = null;
    this.selectedDisplayUrl = null;

    this.resetToPreview();

    // 检查是否是图片 - 通过 MIME 类型或文件扩展名
    const fileName = file.name.toLowerCase();
    const isImage =
      file.type.startsWith("image/") ||
      fileName.endsWith(".jpg") ||
      fileName.endsWith(".jpeg") ||
      fileName.endsWith(".png") ||
      fileName.endsWith(".webp") ||
      fileName.endsWith(".heic") ||
      fileName.endsWith(".gif");

    if (isImage) {
      this.showImagePreview(file);
    } else {
      this.showFilePreview(file);
    }

    this.open();
    const shouldCrop = isImage && this.shouldEnableImageCrop();
    if (shouldCrop) this.showCropState(file);
  }

  showImagePreview(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewImage.src = e.target.result;
      this.previewImage.style.display = "block";
      this.previewFile.style.display = "none";
    };
    reader.readAsDataURL(file);
  }

  shouldEnableImageCrop() {
    let container = document.querySelector("quick-buy-drawer[open], quick-buy-popover[open]") || document.querySelector(".main-product") || document.body;
    let script = container.querySelector(".variant-metafields-data");
    if (!script) {
      const pageScripts = document.querySelectorAll(".variant-metafields-data");
      if (pageScripts.length === 1) script = pageScripts[0];
    }
    if (!script) {
      return false;
    }
    try {
      const productMetafields = JSON.parse(script.textContent).productMetafields || {};
      const isEnabled = (value) => ["true", "yes"].includes(String(value).toLowerCase());
      return isEnabled(productMetafields.isBadge) && isEnabled(productMetafields.enableImageCrop);
    } catch (error) {
      return false;
    }
  }

  showCropState(file) {
    const shouldCrop = this.shouldEnableImageCrop();
    if (!this.cropState || !this.cropImage || !file || !shouldCrop) return;
    this.currentState = "crop";
    this.cropState.style.display = "block";
    document.getElementById("uploadPreviewState").style.display = "none";
    if (this.buttonsContainer) this.buttonsContainer.style.display = "none";
    const title = this.modal.querySelector(".global-upload-modal-title");
    if (title) title.textContent = "Crop Your Image";
    this.applyCropAspectRatio(this.getSelectedCropAspectRatio());
    const reader = new FileReader();
    reader.onload = (event) => {
      this.cropImage.onload = () => {
        this.resetCrop();
      };
      this.cropImage.src = event.target.result;
      if (this.cropClearImage) this.cropClearImage.src = event.target.result;
    };
    reader.readAsDataURL(file);
  }

  getSelectedCropAspectRatio() {
    const container = document.querySelector("quick-buy-drawer[open], quick-buy-popover[open]") || document.querySelector(".main-product") || document.body;
    const form = container.querySelector('form[action*="/cart/add"], form[id*="product-form"]');
    const variantId = form?.querySelector('input[name="id"], select[name="id"]')?.value || this.getCurrentVariantId();
    const script = container.querySelector(".variant-metafields-data") || document.querySelector(".variant-metafields-data");
    try {
      const data = JSON.parse(script?.textContent || "{}");
      const value = data.variantMetafields?.[variantId]?.cropAspectRatio;
      const match = String(value || "1:1").trim().match(/^(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)$/);
      if (match && Number(match[1]) > 0 && Number(match[2]) > 0) {
        return `${match[1]}:${match[2]}`;
      }
    } catch (error) {}
    return "1:1";
  }

  applyCropAspectRatio(ratio) {
    if (!this.cropStage || !this.cropFrame) return;
    const [widthRatio, heightRatio] = String(ratio || "1:1").split(":").map(Number);
    const stageWidth = this.cropStage.clientWidth || 560;
    const stageHeight = this.cropStage.clientHeight || stageWidth;
    const frameRatio = widthRatio / heightRatio || 1;
    const isMobile = window.matchMedia?.("(max-width: 600px)").matches;
    const horizontalMargin = isMobile ? 28 : 50;
    const verticalMargin = isMobile ? 40 : 60;
    const maxWidth = Math.max(1, stageWidth - horizontalMargin * 2);
    const maxHeight = Math.max(1, stageHeight - verticalMargin * 2);
    let frameWidth = maxWidth;
    let frameHeight = frameWidth / frameRatio;
    if (frameHeight > maxHeight) {
      frameHeight = maxHeight;
      frameWidth = frameHeight * frameRatio;
    }
    this.cropFrame.style.inset = "auto";
    this.cropFrame.style.width = `${frameWidth}px`;
    this.cropFrame.style.height = `${frameHeight}px`;
    this.cropFrame.style.left = `${(stageWidth - frameWidth) / 2}px`;
    this.cropFrame.style.top = `${(stageHeight - frameHeight) / 2}px`;
    const frameLeft = (stageWidth - frameWidth) / 2;
    const frameTop = (stageHeight - frameHeight) / 2;
    this.updateCropImageClip(frameLeft, frameTop, frameWidth, frameHeight);
  }

  updateCropImageClip(frameLeft, frameTop, frameWidth, frameHeight) {
    if (!this.cropImage?.naturalWidth) return;
    const renderedWidth = this.cropImage.naturalWidth * this.cropBaseScale * this.cropScale;
    const renderedHeight = this.cropImage.naturalHeight * this.cropBaseScale * this.cropScale;
    const left = Math.max(0, frameLeft - this.cropOffsetX);
    const top = Math.max(0, frameTop - this.cropOffsetY);
    const right = Math.max(0, renderedWidth - (frameLeft + frameWidth - this.cropOffsetX));
    const bottom = Math.max(0, renderedHeight - (frameTop + frameHeight - this.cropOffsetY));
    this.cropImage.style.clipPath = "none";
    if (this.cropClearImage) {
      this.cropClearImage.style.clipPath = `inset(${top}px ${right}px ${bottom}px ${left}px round 12px)`;
    }
  }

  resetCrop() {
    if (!this.cropStage || !this.cropImage?.naturalWidth) return;
    const stageWidth = this.cropStage.clientWidth || 560;
    const stageHeight = this.cropStage.clientHeight || stageWidth;
    const frame = this.cropStage.querySelector(".upload-crop-frame");
    const frameWidth = frame?.clientWidth || stageWidth * 0.72;
    const frameHeight = frame?.clientHeight || stageHeight * 0.72;
    this.cropBaseScale = Math.max(frameWidth / this.cropImage.naturalWidth, frameHeight / this.cropImage.naturalHeight);
    this.cropScale = 1;
    this.cropOffsetX = (stageWidth - this.cropImage.naturalWidth * this.cropBaseScale) / 2;
    this.cropOffsetY = (stageHeight - this.cropImage.naturalHeight * this.cropBaseScale) / 2;
    this.updateCropZoomInput();
    this.renderCropImage();
  }

  getCropStagePoint(clientX, clientY) {
    const rect = this.cropStage.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  getCropFrameCenter() {
    const frame = this.cropStage?.querySelector(".upload-crop-frame");
    if (!this.cropStage || !frame) return { x: 0, y: 0 };
    return {
      x: frame.offsetLeft + frame.clientWidth / 2,
      y: frame.offsetTop + frame.clientHeight / 2,
    };
  }

  setCropScale(nextScale, focalX, focalY) {
    if (!this.cropImage?.naturalWidth) return;
    const oldRenderedScale = this.cropBaseScale * this.cropScale;
    const imageX = (focalX - this.cropOffsetX) / oldRenderedScale;
    const imageY = (focalY - this.cropOffsetY) / oldRenderedScale;
    this.cropScale = Math.min(1.5, Math.max(1, nextScale));
    const newRenderedScale = this.cropBaseScale * this.cropScale;
    this.cropOffsetX = focalX - imageX * newRenderedScale;
    this.cropOffsetY = focalY - imageY * newRenderedScale;
    this.updateCropZoomInput();
    this.renderCropImage();
  }

  stepCropZoom(delta) {
    const focalPoint = this.getCropFrameCenter();
    this.setCropScale(this.cropScale + delta, focalPoint.x, focalPoint.y);
  }

  updateCropZoomInput() {
    if (this.cropZoom) this.cropZoom.value = `${Math.round(this.cropScale * 100)}%`;
  }

  startCropPinch() {
    const points = Array.from(this.cropPointers.values()).slice(0, 2);
    const midpoint = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
    const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
    const renderedScale = this.cropBaseScale * this.cropScale;
    this.cropPinchStart = {
      distance: Math.max(1, distance),
      scale: this.cropScale,
      imageX: (midpoint.x - this.cropOffsetX) / renderedScale,
      imageY: (midpoint.y - this.cropOffsetY) / renderedScale,
    };
  }

  updateCropPinch() {
    const points = Array.from(this.cropPointers.values()).slice(0, 2);
    if (points.length < 2 || !this.cropPinchStart) return;
    const midpoint = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
    const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
    this.cropScale = Math.min(1.5, Math.max(1, this.cropPinchStart.scale * distance / this.cropPinchStart.distance));
    const renderedScale = this.cropBaseScale * this.cropScale;
    this.cropOffsetX = midpoint.x - this.cropPinchStart.imageX * renderedScale;
    this.cropOffsetY = midpoint.y - this.cropPinchStart.imageY * renderedScale;
    this.updateCropZoomInput();
    this.renderCropImage();
  }

  clampCropOffset() {
    if (!this.cropStage || !this.cropImage?.naturalWidth) return;
    const stageWidth = this.cropStage.clientWidth;
    const stageHeight = this.cropStage.clientHeight;
    const frame = this.cropStage.querySelector(".upload-crop-frame");
    const frameWidth = frame?.clientWidth || stageWidth * 0.72;
    const frameHeight = frame?.clientHeight || stageHeight * 0.72;
    const frameLeft = (stageWidth - frameWidth) / 2;
    const frameTop = (stageHeight - frameHeight) / 2;
    const width = this.cropImage.naturalWidth * this.cropBaseScale * this.cropScale;
    const height = this.cropImage.naturalHeight * this.cropBaseScale * this.cropScale;
    this.cropOffsetX = Math.min(frameLeft, Math.max(frameLeft + frameWidth - width, this.cropOffsetX));
    this.cropOffsetY = Math.min(frameTop, Math.max(frameTop + frameHeight - height, this.cropOffsetY));
  }

  renderCropImage() {
    if (!this.cropImage?.naturalWidth) return;
    this.clampCropOffset();
    const scale = this.cropBaseScale * this.cropScale;
    this.cropImage.style.width = `${this.cropImage.naturalWidth * scale}px`;
    this.cropImage.style.height = `${this.cropImage.naturalHeight * scale}px`;
    this.cropImage.style.transform = `translate(${this.cropOffsetX}px, ${this.cropOffsetY}px)`;
    if (this.cropClearImage) {
      this.cropClearImage.style.width = `${this.cropImage.naturalWidth * scale}px`;
      this.cropClearImage.style.height = `${this.cropImage.naturalHeight * scale}px`;
      this.cropClearImage.style.transform = `translate(${this.cropOffsetX}px, ${this.cropOffsetY}px)`;
    }
    const frame = this.cropStage.querySelector(".upload-crop-frame");
    if (frame) {
      this.updateCropImageClip(frame.offsetLeft, frame.offsetTop, frame.clientWidth, frame.clientHeight);
    }
  }

  async confirmCrop() {
    if (!this.currentFile || !this.cropImage?.naturalWidth || !this.cropStage) return;
    const frame = this.cropStage.querySelector(".upload-crop-frame");
    const frameWidth = frame.clientWidth;
    const frameHeight = frame.clientHeight;
    const frameLeft = (this.cropStage.clientWidth - frameWidth) / 2;
    const frameTop = (this.cropStage.clientHeight - frameHeight) / 2;
    const scale = this.cropBaseScale * this.cropScale;
    const sx = Math.max(0, Math.round((frameLeft - this.cropOffsetX) / scale));
    const sy = Math.max(0, Math.round((frameTop - this.cropOffsetY) / scale));
    const sw = Math.min(this.cropImage.naturalWidth - sx, Math.round(frameWidth / scale));
    const sh = Math.min(this.cropImage.naturalHeight - sy, Math.round(frameHeight / scale));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, sw);
    canvas.height = Math.max(1, sh);
    canvas.getContext("2d").drawImage(this.cropImage, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    const outputFormat = this.getCropOutputFormat(this.currentFile);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, outputFormat.mimeType, outputFormat.quality),
    );
    if (!blob) return;
    const baseName = (this.currentFile.name || "upload").replace(/\.[^.]+$/, "");
    this.currentFile = new File(
      [blob],
      `${baseName}-cropped.${outputFormat.extension}`,
      { type: outputFormat.mimeType, lastModified: Date.now() },
    );
    this.originalFile = this.currentFile;
    this.cropState.style.display = "none";
    document.getElementById("uploadPreviewState").style.display = "block";
    if (this.buttonsContainer) this.buttonsContainer.style.display = "flex";
    this.resetToPreview();
    this.showImagePreview(this.currentFile);
  }

  getCropOutputFormat(file) {
    const mimeType = String(file?.type || "").toLowerCase();
    const extension = String(file?.name || "").split(".").pop().toLowerCase();

    if (mimeType === "image/png" || extension === "png") {
      return { mimeType: "image/png", extension: "png" };
    }
    if (mimeType === "image/webp" || extension === "webp") {
      return { mimeType: "image/webp", extension: "webp", quality: 0.92 };
    }
    if (mimeType === "image/gif" || extension === "gif") {
      return { mimeType: "image/png", extension: "png" };
    }

    // HEIC/HEIF is converted to a browser-decodable JPEG before this modal.
    return { mimeType: "image/jpeg", extension: "jpg", quality: 0.92 };
  }

  /**
   * Helper to create a DOM overlay with watermarks (bottom 2 rows only)
   * Uses CSS to ensure robust positioning without JS calculations.
   * Creates a square box at the bottom of the container.
   * @param {boolean} isSmall - Whether to create a smaller watermark for thumbnails
   * @returns {HTMLElement} - the overlay element
   */
  createWatermarkOverlay(isSmall = false, isHistory = false) {
    // 1. Outer wrapper (absolute 100%)
    const overlay = document.createElement("div");
    overlay.className = "watermark-overlay";
    overlay.style.position = "absolute";
    overlay.style.top = "0";
    overlay.style.left = "0";
    overlay.style.width = "100%";
    overlay.style.height = "100%";
    overlay.style.pointerEvents = "none";
    overlay.style.zIndex = "10";
    overlay.style.overflow = "hidden";
    overlay.style.opacity = isSmall ? "0.25" : "0.3";

    const logoUrl = window.THEME_CONFIG?.logoUrl || "assets/logo.png";

    // For small thumbnails, use a simpler single centered watermark
    if (isSmall) {
      const centerContainer = document.createElement("div");
      centerContainer.style.position = "absolute";
      centerContainer.style.top = "50%";
      centerContainer.style.left = "50%";
      centerContainer.style.transform = "translate(-50%, -50%) rotate(-45deg)";
      centerContainer.style.display = "flex";
      centerContainer.style.alignItems = "center";
      centerContainer.style.justifyContent = "center";

      const img = document.createElement("img");
      img.src = logoUrl;
      img.style.width = "60%";
      img.style.maxWidth = "50px";
      img.style.objectFit = "contain";

      centerContainer.appendChild(img);
      overlay.appendChild(centerContainer);

      return overlay;
    }

    // 2. Bottom Square Container (for full-size watermark)
    const gridContainer = document.createElement("div");
    gridContainer.style.position = "absolute";
    gridContainer.style.bottom = "0";
    gridContainer.style.left = "0";
    gridContainer.style.width = "100%";
    gridContainer.style.height = "0";
    gridContainer.style.paddingBottom = "100%";

    // 3. The Grid
    const grid = document.createElement("div");
    grid.style.position = "absolute";
    grid.style.top = "0";
    grid.style.left = "0";
    grid.style.width = "100%";
    grid.style.height = "100%";
    grid.style.paddingTop = "25%";
    grid.style.boxSizing = "border-box";
    grid.style.display = "grid";
    grid.style.gridTemplateColumns = "1fr 1fr";
    grid.style.gridTemplateRows = "1fr 1fr";

    // Create 4 cells - use different offsets for history vs current generation
    const isMobile = window.innerWidth <= 768;
    for (let i = 0; i < 4; i++) {
      const cell = document.createElement("div");
      cell.style.display = "flex";
      cell.style.alignItems = "center";
      cell.style.justifyContent = "center";

      const col = i % 2;  // 0 or 1 (left or right)
      const row = Math.floor(i / 2);  // 0 or 1 (top or bottom)

      let offset, offsetY;
      if (isHistory) {
        offset = isMobile ? 30 : 35;
        offsetY = isMobile ? 60 : 73;
      } else {
        offset = isMobile ? 45 : 60;
        offsetY = isMobile ? 120 : 130;
      }

      const translateX = col === 0 ? -offset : offset;
      const translateY = row === 0 ? -offsetY : 0;  // Only top row has Y offset
      cell.style.transform = `translate(${translateX}px, ${translateY}px) rotate(-45deg)`;

      const img = document.createElement("img");
      img.src = logoUrl;
      img.style.maxWidth = "40%";
      img.style.maxHeight = "40%";
      img.style.objectFit = "contain";
      img.style.position = "unset";
      img.style.transform = "scale(1.5)";

      cell.appendChild(img);
      grid.appendChild(cell);
    }

    gridContainer.appendChild(grid);
    overlay.appendChild(gridContainer);

    return overlay;
  }

  showFilePreview(file) {
    const fileInfo = this.previewFile.querySelector(".file-info");
    fileInfo.querySelector(".file-name").textContent = file.name;
    fileInfo.querySelector(".file-size").textContent = this.formatFileSize(
      file.size
    );

    this.previewImage.style.display = "none";
    this.previewFile.style.display = "flex";
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  setGenerateSubmitting(isSubmitting) {
    this.isGenerateSubmitting = isSubmitting;

    [this.betterButton].forEach((button) => {
      if (!button) return;

      button.disabled = isSubmitting;
      button.setAttribute("aria-busy", isSubmitting ? "true" : "false");
    });
  }

  open() {
    // Calculate scrollbar width before hiding it
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    // Add padding to compensate for scrollbar removal
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      // Find likely fixed/sticky positioned elements (performance optimized)
      const potentialElements = document.querySelectorAll(
        'header, nav, .header, .shopify-section, [class*="sticky"], [class*="fixed"], [id*="shopify-section"]'
      );

      potentialElements.forEach((el) => {
        const computedStyle = window.getComputedStyle(el);
        const position = computedStyle.position;

        if (
          position === "fixed" ||
          position === "sticky" ||
          position === "-webkit-sticky"
        ) {
          const currentPaddingRight = computedStyle.paddingRight;
          const currentPaddingValue = parseFloat(currentPaddingRight) || 0;
          el.style.paddingRight = `${currentPaddingValue + scrollbarWidth}px`;
          el.setAttribute("data-modal-original-padding", currentPaddingRight);
        }
      });
    }

    document.body.style.overflow = "hidden";
    this.modal.classList.add("active");
  }

  close() {
    if (!this.modal || !this.modal.classList.contains("active")) {
      return;
    }

    // Track analytics event based on current state
    if (typeof GtagUtils !== "undefined") {
      const fileName = this.currentFile ? this.currentFile.name : "";
      const fileSize = this.currentFile ? this.currentFile.size : 0;

      switch (this.currentState) {
        case "preview":
          // User closed the preview without confirming upload
          GtagUtils.trackUploadPreviewClose(fileName, fileSize);
          break;
        case "loading":
          // User closed during generation
          const progressPercentage = this.progressAnimation
            ? Math.round(this.progressAnimation.getCurrentProgress())
            : 0;
          GtagUtils.trackGenerateLoadingClose(progressPercentage);
          break;
        case "selection":
          // User closed design selection without confirming
          GtagUtils.trackDesignSelectClose(this.generatedImageCount);
          break;
      }
    }

    // Unlock all drawers and popovers (there may be multiple)
    const allDrawers = document.querySelectorAll("quick-buy-drawer");
    const allPopovers = document.querySelectorAll("quick-buy-popover");
    allDrawers.forEach((drawer) => {
      drawer.dataset.modalOpen = "false";
    });
    allPopovers.forEach((popover) => {
      popover.dataset.modalOpen = "false";
    });

    const drawer = document.querySelector("quick-buy-drawer[open]");
    const popover = document.querySelector("quick-buy-popover[open]");

    // Cancel ongoing requests
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Stop progress animation
    if (this.progressAnimation) {
      this.progressAnimation.stop();
      this.progressAnimation = null;
    }

    // Stop carousel animation
    this.stopCarousel();

    if (this.closeResetTimer) {
      clearTimeout(this.closeResetTimer);
      this.closeResetTimer = null;
    }

    // Remove beforeunload listener
    window.removeEventListener("beforeunload", this.handleBeforeUnload);

    this.modal.classList.remove("active");

    // Check if in drawer or popover - if in either, don't restore body overflow
    if (!drawer && !popover) {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";

      // Restore padding for fixed/sticky elements
      const elementsWithPadding = document.querySelectorAll(
        "[data-modal-original-padding]"
      );
      elementsWithPadding.forEach((el) => {
        el.style.paddingRight = el.getAttribute("data-modal-original-padding");
        el.removeAttribute("data-modal-original-padding");
      });
    }

    if (this.activeFileInput) {
      this.activeFileInput.value = "";
    }
    this.activeFileInput = null;
    this.currentFile = null;
    this.originalFile = null;
    this.confirmCallback = null;
    this.regenerateCallback = null;
    this.selectedImageUrl = null;
    this.selectedDisplayUrl = null;
    this.selectedJobId = null;
    this.selectedImageModelName = null;

    this.setGenerateSubmitting(false);

    // Reset analytics state
    this.currentState = "preview";
    this.generationStartTime = null;
    this.generatedImageCount = 0;
    this.currentSourceType = null;

    // Reset regeneration counter when modal is closed
    this.regenerateCount = 0;

    // Reset history selection flag
    this.isFromHistory = false;
    this.selectedHistoryUrl = null;
    this.selectedHistoryDisplayUrl = null;
    this.selectedHistoryModelName = null;

    this.closeResetTimer = setTimeout(() => {
      this.closeResetTimer = null;
      if (!this.modal.classList.contains("active")) {
        this.resetToPreview();
        // Reset fridge magnet flag AFTER resetToPreview (because resetToPreview calls detectIsBadge which may set it again)
        this._isFridgeMagnet = false;
      }
    }, 320);

    document.body.style.overflow = "unset";
  }

  async confirm(sourceType) {
    if (sourceType) {
      if (this.isGenerateSubmitting) {
        return;
      }

      this.setGenerateSubmitting(true);
    }

    // Store sourceType for model_name tracking (only when starting generation)
    if (sourceType) {
      this.currentSourceType = sourceType;
      if (typeof GtagUtils !== "undefined" && typeof GtagUtils.setFlowContext === "function") {
        GtagUtils.setFlowContext({ generation_model: this.getCurrentModelName() });
      }
    }

    // Track analytics event based on current state
    if (typeof GtagUtils !== "undefined") {
      if (this.currentState === "preview" && this.currentFile) {
        // User confirmed upload from preview state
        GtagUtils.trackUploadConfirm(
          this.currentFile.name,
          this.currentFile.size
        );
      } else if (this.currentState === "selection" && this.selectedImageUrl) {
        // User confirmed design selection
        GtagUtils.trackDesignConfirm(this.selectedJobId, {
          generation_model: this.selectedImageModelName || this.getCurrentModelName(),
        });
      }
    }

    // If selecting from history, just close the modal (no generation needed)
    if (this.isFromHistory && this.selectedImageUrl && !sourceType) {
      // Call callback with null file to indicate history selection
      // The callback can get the selected URL via getSelectedImageUrl()
      if (this.confirmCallback) {
        this.confirmCallback(null, null);
      }
      this.close();
      return;
    }

    // [DISABLED] Face detection check - entire block disabled
    // if (sourceType && this.currentFile && this.isSinglePersonSelected()) {
    //   try {
    //     // Show loading indicator on buttons
    //     const quickBtn = this.quickButton;
    //     const betterBtn = this.betterButton;
    //     const originalQuickText = quickBtn?.innerHTML;
    //     const originalBetterText = betterBtn?.innerHTML;

    //     // Disable buttons and show loading
    //     if (quickBtn) {
    //       quickBtn.disabled = true;
    //       quickBtn.innerHTML = '<span class="btn-name">Analyzing...</span>';
    //     }
    //     if (betterBtn) {
    //       betterBtn.disabled = true;
    //       betterBtn.innerHTML = '<span class="btn-name">Analyzing...</span>';
    //     }

    //     // Detect faces
    //     const faceCount = await this.detectFaces(this.currentFile);

    //     // Restore buttons
    //     if (quickBtn) {
    //       quickBtn.disabled = false;
    //       quickBtn.innerHTML = originalQuickText;
    //     }
    //     if (betterBtn) {
    //       betterBtn.disabled = false;
    //       betterBtn.innerHTML = originalBetterText;
    //     }

    //     // If no face detected, show warning and block generation
    //     // if (faceCount === 0) {
    //     //   this.showNoFaceWarning();
    //     //   // Don't proceed with generation - let user change photo
    //     //   return;
    //     // }

    //     // If multiple faces detected, show warning and block generation
    //     // [DISABLED] Multi-face detection temporarily disabled
    //     // if (faceCount > 1) {
    //     //   this.showMultiFaceWarning(faceCount);
    //     //   // Don't proceed with generation - warning will close the modal
    //     //   return;
    //     // }
    //   } catch (error) {
    //     // Continue with generation even if face detection fails
    //   }
    // }

    try {
      if (this.confirmCallback) {
        // 调用确认回调，传递文件和 sourceType
        // 注意：不再自动关闭弹窗，由回调函数控制
        this.confirmCallback(this.currentFile, sourceType);
      } else if (this.currentFile) {
        // 向后兼容：如果没有回调，只是关闭弹窗
        this.close();
      }
    } catch (error) {
      if (sourceType) {
        this.setGenerateSubmitting(false);
      }
      throw error;
    }
  }

  /**
   * Regenerate
   */
  regenerate() {
    // Check if regeneration limit has been reached
    if (this.regenerateCount >= this.maxRegenerateCount) {
      return;
    }

    // Increment regeneration counter
    this.regenerateCount++;

    // Track regenerate click event with current model_name
    if (typeof GtagUtils !== "undefined") {
      const modelName = this.getCurrentModelName();
      GtagUtils.trackDesignRegenerateClick(modelName, this.generatedImageCount);
    }

    // Reset to progress state and call regenerate callback with original file
    if (this.regenerateCallback && this.originalFile) {
      // Reset state
      this.selectedImageUrl = null;
      this.selectedDisplayUrl = null;
      this.generatedImageCount = 0;

      // Hide regenerate button during generation
      if (this.regenerateButton) {
        this.regenerateButton.style.display = "none";
      }

      // Show progress state
      this.showProgress();

      const progressDuration =
        this.currentSourceType === "cute_single_img2img_deliver3d_sd"
          ? 35000
          : 47000;
      // Start progress animation
      this.progressAnimation = this.startProgressAnimation(progressDuration);

      // Call the regenerate callback with the original file
      this.regenerateCallback(this.originalFile);
    }
  }

  /**
   * 切换到进度条状态
   */
  showProgress() {
    // Update analytics state
    this.currentState = "loading";
    this.generationStartTime = Date.now();

    const previewState = document.getElementById("uploadPreviewState");
    const progressState = document.getElementById("uploadProgressState");
    const imageSelectionState = document.getElementById("imageSelectionState");
    const title = this.modal.querySelector(".global-upload-modal-title");

    if (previewState) previewState.style.display = "none";
    if (progressState) progressState.style.display = "block";
    if (imageSelectionState) imageSelectionState.style.display = "none";
    if (title) {
      title.textContent = "Generating your preview";
      title.style.display = "block";
    }

    // Hide all buttons during progress
    if (this.buttonsContainer) this.buttonsContainer.style.display = "none";

    // Hide Recent Result button during progress
    if (this.btnRecentResult) this.btnRecentResult.style.display = "none";

    // Hide promo notice during progress
    const notice = this.modal.querySelector(".upload-info-notice");
    if (notice) notice.style.display = "none";

    // 初始化进度条为 1%
    this.updateProgress(1);

    // 启动轮播动画
    this.startCarousel();

    // 创建新的 AbortController
    this.abortController = new AbortController();

    // Add beforeunload listener to track generation abandonment
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  }

  /**
   * 更新进度
   * @param {number} percentage - 进度百分比 (1-100)
   */
  updateProgress(percentage) {
    const progressBar = document.getElementById("uploadProgressBar");
    const progressText = document.getElementById("uploadProgressText");

    if (progressBar) {
      progressBar.style.width = percentage + "%";
    }
    if (progressText) {
      progressText.textContent = `${Math.round(percentage)}%`;
    }
  }

  /**
   * 启动渐进式进度条（使用缓动函数自然增长到99%）
   * @param {number} durationMs - 到达99%所需的时间（毫秒），默认55秒
   * @returns {Object} 包含 stop 方法的对象
   */
  startProgressAnimation(durationMs = 47000) {
    let progress = 1;
    let animationId;
    const targetPercent = 99;
    const duration = durationMs;
    const startTime = Date.now();

    const animate = () => {
      if (progress < targetPercent) {
        const elapsed = Date.now() - startTime;
        const timeProgress = Math.min(elapsed / duration, 1); // 0-1之间

        // 使用缓动函数（easeOutCubic）使动画更自然
        // 开始快，逐渐变慢
        const easedProgress = 1 - Math.pow(1 - timeProgress, 2);

        // 计算当前进度：从1%到96%
        progress = 1 + (targetPercent - 1) * easedProgress;

        this.updateProgress(progress);

        // 如果还未到达目标时间，继续动画
        if (timeProgress < 1) {
          animationId = requestAnimationFrame(animate);
        }
      }
    };

    animate();

    const progressAnimationObj = {
      stop: () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      },
      getCurrentProgress: () => progress,
    };

    // 保存到实例变量
    this.progressAnimation = progressAnimationObj;

    return progressAnimationObj;
  }

  /**
   * 初始化图片选择状态（显示4个loading占位符）
   */
  initImageSelection(imageCount = 2) {
    // Update analytics state
    this.currentState = "selection";
    this.generatedImageCount = 0; // Will be updated as images load
    this.setGenerateSubmitting(false);

    // Remove beforeunload listener since generation phase is complete
    window.removeEventListener("beforeunload", this.handleBeforeUnload);

    const previewState = document.getElementById("uploadPreviewState");
    const progressState = document.getElementById("uploadProgressState");
    const imageSelectionState = document.getElementById("imageSelectionState");
    const imagesGrid = document.getElementById("select-image-list");
    const title = this.modal.querySelector(".global-upload-modal-title");

    // 停止轮播动画
    this.stopCarousel();

    if (previewState) previewState.style.display = "none";
    if (progressState) progressState.style.display = "none";
    if (imageSelectionState) imageSelectionState.style.display = "block";
    if (title) title.style.display = "none";

    // Show buttons container, hide generate buttons, show confirm button
    if (this.buttonsContainer) {
      this.buttonsContainer.style.display = "flex";
      // Remove single-button class in selection state (two buttons: Regenerate + Confirm)
      this.buttonsContainer.classList.remove("single-button", "single-button-center");
    }
    if (this.betterButton) this.betterButton.style.display = "none";
    if (this.confirmButton) {
      this.confirmButton.style.display = "inline-block";
      this.confirmButton.textContent = "Confirm Selection";
      this.confirmButton.disabled = true; // 初始时禁用，直到有图片加载完成
    }

    // Show regenerate button in selection state
    if (this.regenerateButton) {
      this.regenerateButton.style.display = "inline-block";
      // Disable regenerate button if limit has been reached
      if (this.regenerateCount >= this.maxRegenerateCount) {
        this.regenerateButton.disabled = true;
        this.regenerateButton.title = "Regeneration limit reached (5 attempts)";
      } else {
        this.regenerateButton.disabled = false;
        this.regenerateButton.title = "";
      }
    }

    // Show Recent Result button in selection state (after generation completes)
    if (this.btnRecentResult) this.btnRecentResult.style.display = "flex";

    // Always show promo notice
    const notice = this.modal.querySelector(".upload-info-notice");
    const currentStyle = this.getCurrentStyleRaw();
    const isPhone = this.detectIsPhone();
    const isAromaStone = this.detectIsAromaStone();

    // 检查是否为需要显示提示的特殊风格
    const specialStyles = ["3D Embossed NL","Line Healing Style","3D Oil Painting","3D Oil Relief - Natural Wood","3D Oil Relief - Walnut"];
    const shouldShow = specialStyles.includes(currentStyle);
    if (notice && (!shouldShow || isAromaStone) && !isPhone) {
      this.updateUploadInfoNoticeText(notice);
      notice.style.display = "flex";
    }

    // 初始化大预览区域为loading状态
    const previewMain = document.getElementById("image-preview-main");
    if (previewMain) {
      previewMain.innerHTML = '<div class="image-loading-spinner"></div>';
      previewMain.setAttribute("data-url", "");
    }

    // Generate loading placeholders for the requested number of results.
    if (imagesGrid) {
      imagesGrid.innerHTML = Array.from(
        { length: Math.max(1, Number(imageCount) || 2) },
        (_, index) => `
        <div class="image-thumbnail" data-index="${index}" data-url="">
          <div class="image-loading-spinner"></div>
        </div>
      `
      ).join("");
    }

    // 根据当前风格显示/隐藏特殊提示
    this.updateSpecialStyleNotice();
  }

  /**
   * 更新特殊风格提示文案的显示状态
   */
  updateSpecialStyleNotice() {
    const noticeElement = document.getElementById("special-style-notice");
    if (!noticeElement) return;

    // 获取当前选择的风格（原始值，非映射后的API格式）
    const currentStyle = this.getCurrentStyleRaw();

    // 检查是否为需要显示提示的特殊风格
    const specialStyles = ["3D Embossed NL","Line Healing Style","3D Oil Painting","3D Oil Relief - Natural Wood","3D Oil Relief - Walnut"];
    const shouldShow = specialStyles.includes(currentStyle);

    const isPhone = this.detectIsPhone();

    if (shouldShow || isPhone) {
      // 获取 WhatsApp 链接
      const whatsappLink = this.getWhatsAppLink();

      // 更新提示内容
      noticeElement.innerHTML = `
        Please contact us after ordering, or we will reach out via
        <a href="mailto:support@snapfigures.com" style="color: #111827;font-weight: 600;text-decoration: underline;">Email</a>
        /
        <a href="${whatsappLink}" target="_blank" rel="noopener noreferrer" style="color: #25D366;font-weight:600; text-decoration: underline;">WhatsApp</a>
        to confirm your design.
      `;
      noticeElement.style.display = "block";
    } else {
      noticeElement.style.display = "none";
    }
  }

  /**
   * 获取当前选择的风格原始值（未映射）
   * @returns {string} 原始风格值
   */
  getCurrentStyleRaw() {
    // Try to find the active container (drawer, popover, or main page)
    let container = document.querySelector("quick-buy-drawer[open]");
    if (!container) {
      container = document.querySelector("quick-buy-popover[open]");
    }
    if (!container) {
      container = document.querySelector(".main-product") || document.body;
    }

    // Method 1: Get from global variable (priority)
    const selectedOptions = window.selectedProductOptions;
    if (selectedOptions && selectedOptions.size) {
      // 注意：这里用 size，因为 size 和 style 在赋值时搞反了
      return selectedOptions.size;
    }

    // Method 2: Find all checked radio buttons in container
    const allCheckedRadios = container.querySelectorAll(
      'input[type="radio"][name^="option"]:checked'
    );

    // Smart find style option: check if value is in mapping
    for (const radio of allCheckedRadios) {
      const value = radio.value;
      if (this.styleMapping[value]) {
        return value;
      }
    }

    // Method 3: Get from display value in container (fallback)
    const allValueDisplays = container.querySelectorAll(
      '[id*="option"][id*="-value"]'
    );
    for (const display of allValueDisplays) {
      const value = display.textContent.trim();
      if (this.styleMapping[value]) {
        return value;
      }
    }

    // Method 4: Get from option selectors in container (final fallback)
    const allOptionSelectors = container.querySelectorAll(
      ".product-form__option-selector"
    );
    for (let index = 0; index < allOptionSelectors.length; index++) {
      const selector = allOptionSelectors[index];
      const checkedInput = selector.querySelector("input:checked");

      if (checkedInput && this.styleMapping[checkedInput.value]) {
        return checkedInput.value;
      }
    }

    return "";
  }

  /**
   * 获取 WhatsApp 链接
   */
  getWhatsAppLink() {
    // 尝试从页面中的社交媒体链接获取
    const whatsappSocialLink = document.querySelector('.social-media__item--whatsapp a');
    if (whatsappSocialLink) {
      return whatsappSocialLink.href;
    }

    // 默认 WhatsApp 链接
    return "https://api.whatsapp.com/send/?phone=8613652438402&text&type=phone_number&app_absent=0"; // 需要替换为实际的 WhatsApp 号码
  }

  /**
   * 更新单个图片槽位
   * @param {number} index - 图片索引 (0-3)
   * @param {string} url - 图片URL (fileKeyImg0)
   * @param {string} displayUrl - Display image URL (fileKeyImg1)
   */
  updateImageSlot(index, url, displayUrl, jobId) {
    const imagesGrid = document.getElementById("select-image-list");
    if (!imagesGrid) return;

    const imageThumbnail = imagesGrid.querySelector(`[data-index="${index}"]`);
    if (!imageThumbnail) return;

    // Increment generated image count for analytics
    this.generatedImageCount++;

    // Update session tracking data for order_placed event
    if (typeof GtagTrackingHelpers !== "undefined") {
      const modelName = this.getCurrentModelName();
      GtagTrackingHelpers.incrementGenerationCount(1, modelName);
    }

    // 更新缩略图 (Update thumbnail)
    imageThumbnail.classList.remove("loading");
    imageThumbnail.setAttribute("data-url", url);
    imageThumbnail.setAttribute("data-display-url", displayUrl || "");
    imageThumbnail.setAttribute("data-job-id", jobId || "");
    const modelName = this.getImageModelName(index);
    imageThumbnail.setAttribute("data-model-name", modelName);
    imageThumbnail.innerHTML = "";

    // 创建缩略图容器（用于添加水印）
    const thumbContainer = document.createElement("div");
    thumbContainer.style.width = "100%";
    thumbContainer.style.height = "100%";
    thumbContainer.style.position = "relative";
    thumbContainer.style.overflow = "hidden";

    // 创建缩略图图片
    const thumbImg = new Image();
    thumbImg.style.width = "100%";
    thumbImg.style.height = "100%";
    thumbImg.style.objectFit = "contain";
    thumbImg.oncontextmenu = () => false;

    thumbImg.onload = () => {
      // 添加缩略图水印 (disabled)
      // const thumbWatermark = this.createWatermarkOverlay(true);
      // thumbContainer.appendChild(thumbWatermark);
    };

    thumbImg.src = displayUrl || url;
    thumbContainer.appendChild(thumbImg);
    imageThumbnail.appendChild(thumbContainer);

    // 如果是第一张完成的图片，自动选中它并更新预览
    const allThumbnails = imagesGrid.querySelectorAll(".image-thumbnail");
    const hasSelected = Array.from(allThumbnails).some((opt) =>
      opt.classList.contains("selected")
    );

    if (!hasSelected) {
      imageThumbnail.classList.add("selected");
      this.selectedImageUrl = url;
      this.selectedDisplayUrl = displayUrl || "";
      this.selectedJobId = jobId || "";
      this.selectedImageModelName = modelName;
      this.updatePreviewImage(displayUrl || url);

      // 启用确认按钮
      if (this.confirmButton) {
        this.confirmButton.disabled = false;
      }
    }

    // Add click event for thumbnail
    imageThumbnail.addEventListener("click", () => {
      allThumbnails.forEach((opt) => opt.classList.remove("selected"));
      imageThumbnail.classList.add("selected");
      this.selectedImageUrl = url;
      this.selectedDisplayUrl = displayUrl || "";
      this.selectedJobId = jobId || "";
      this.selectedImageModelName = modelName;
      this.updatePreviewImage(displayUrl || url);
    });
  }

  /**
   * 更新大预览图
   * @param {string} url - 图片URL
   */
  updatePreviewImage(url) {
    const previewMain = document.getElementById("image-preview-main");
    if (!previewMain) return;

    previewMain.setAttribute("data-url", url);
    previewMain.innerHTML = "";

    const container = document.createElement("div");
    container.style.width = "100%";
    container.style.height = "100%";
    container.style.position = "relative";
    container.style.display = "flex";
    container.style.alignItems = "center";
    container.style.justifyContent = "center";
    container.style.overflow = "hidden";

    const img = new Image();
    img.style.width = "100%";
    img.style.height = "100%";
    img.style.objectFit = "contain";
    img.oncontextmenu = () => false;

    img.onload = () => {
      // const watermarkOverlay = this.createWatermarkOverlay();
      // container.appendChild(watermarkOverlay);
    };

    img.src = url;
    container.appendChild(img);
    previewMain.appendChild(container);
  }

  /**
   * Update image slot to show failed state
   * @param {number} index - Image index (0-3)
   */
  updateImageSlotFailed(index) {
    const imagesGrid = document.getElementById("select-image-list");
    if (!imagesGrid) return;

    const imageThumbnail = imagesGrid.querySelector(`[data-index="${index}"]`);
    if (!imageThumbnail) return;

    // Update to show failed state
    imageThumbnail.classList.remove("loading");
    imageThumbnail.classList.add("failed");
    imageThumbnail.setAttribute("data-url", "");
    imageThumbnail.innerHTML = `
      <div class="image-failed-state">
        <svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="22" stroke="#ef4444" stroke-width="2" fill="none"/>
          <path d="M16 16L32 32M32 16L16 32" stroke="#ef4444" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </div>
    `;

    // Make it non-clickable
    imageThumbnail.style.cursor = "not-allowed";
    imageThumbnail.style.opacity = "0.6";
    imageThumbnail.style.display = "none";
  }

  /**
   * 切换到图片选择状态（兼容旧方法）
   * @param {Array} images - 图片数组 [{url, jobId}, ...]
   * @param {boolean} isFromHistory - 是否从历史记录选择
   */
  showImageSelection(images, isFromHistory = false) {
    // Update state
    this.currentState = "selection";
    this.isFromHistory = isFromHistory;

    const previewState = document.getElementById("uploadPreviewState");
    const progressState = document.getElementById("uploadProgressState");
    const imageSelectionState = document.getElementById("imageSelectionState");
    const imagesGrid = document.getElementById("select-image-list");
    const title = this.modal.querySelector(".global-upload-modal-title");

    if (previewState) previewState.style.display = "none";
    if (progressState) progressState.style.display = "none";
    if (imageSelectionState) imageSelectionState.style.display = "block";
    if (title) title.style.display = "none";

    // Show buttons container, hide generate buttons, show confirm button
    if (this.buttonsContainer) {
      this.buttonsContainer.style.display = "flex";
      // Remove single-button class in selection state
      this.buttonsContainer.classList.remove("single-button", "single-button-center");
    }
    if (this.betterButton) this.betterButton.style.display = "none";
    if (this.confirmButton) {
      this.confirmButton.style.display = "inline-block";
      this.confirmButton.textContent = "Confirm Selection";
    }

    // Hide regenerate button when selecting from history (no need to regenerate)
    if (this.regenerateButton) {
      this.regenerateButton.style.display = "none";
    }

    // Show Recent Result button when selecting from history
    if (this.btnRecentResult) this.btnRecentResult.style.display = "flex";

    // Show promo notice in history view
    const notice = this.modal.querySelector(".upload-info-notice");
     const selectedOptions = window.selectedProductOptions;
    const currentStyle = selectedOptions?.size || ""; // 注意：这里用 size，因为 size 和 style 在赋值时搞反了

    // 检查是否为需要显示提示的特殊风格
    const specialStyles = ["3D Embossed NL","Line Healing Style","3D Oil Painting","3D Oil Relief - Natural Wood","3D Oil Relief - Walnut"];
    const shouldShow = specialStyles.includes(currentStyle);
    const isAromaStone = this.detectIsAromaStone();
    if (notice && (!shouldShow || isAromaStone)) {
      this.updateUploadInfoNoticeText(notice);
      notice.style.display = "flex";
    }

    // 生成缩略图和预览图
    if (imagesGrid && images.length > 0) {
      // 生成缩略图
      imagesGrid.innerHTML = images
        .map(
          (img, index) => `
        <div class="image-thumbnail ${
          index === 0 ? "selected" : ""
        }" data-index="${index}" data-url="${img.url}" data-display-url="${img.displayUrl || ""}" data-job-id="${img.jobId || ""}" data-model-name="${img.modelName || this.getImageModelName(index)}">
          <div class="thumb-container" style="width:100%;height:100%;position:relative;overflow:hidden;">
            <img src="${img.displayUrl || img.url}" alt="Option ${
            index + 1
          }" loading="lazy" style="width:100%;height:100%;object-fit:contain;" oncontextmenu="return false" />
          </div>
        </div>
      `
        )
        .join("");

      // 为每个缩略图添加水印
      const imageThumbnails = imagesGrid.querySelectorAll(".image-thumbnail");
      imageThumbnails.forEach((thumbnail) => {
        const thumbContainer = thumbnail.querySelector(".thumb-container");
        const imgEl = thumbContainer.querySelector("img");
        // if (imgEl.complete) {
        //   const watermark = this.createWatermarkOverlay(true);
        //   thumbContainer.appendChild(watermark);
        // } else {
        //   imgEl.onload = () => {
        //     const watermark = this.createWatermarkOverlay(true);
        //     thumbContainer.appendChild(watermark);
        //   };
        // }
      });

      // 默认选中第一张并显示预览
      this.selectedImageUrl = images[0]?.url || "";
      this.selectedDisplayUrl = images[0]?.displayUrl || "";
      this.selectedJobId = images[0]?.jobId || "";
      this.selectedImageModelName = images[0]?.modelName || this.getImageModelName(0);
      this.updatePreviewImage(images[0]?.displayUrl || images[0]?.url);

      // 设置选择事件
      imageThumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener("click", (e) => {
          e.stopPropagation();
          imageThumbnails.forEach((opt) => opt.classList.remove("selected"));
          thumbnail.classList.add("selected");
          const url = thumbnail.getAttribute("data-url");
          const dispUrl = thumbnail.getAttribute("data-display-url") || "";
          const jobId = thumbnail.getAttribute("data-job-id") || "";
          const modelName = thumbnail.getAttribute("data-model-name") || "";
          this.selectedImageUrl = url;
          this.selectedDisplayUrl = dispUrl;
          this.selectedJobId = jobId;
          this.selectedImageModelName = modelName;
          this.updatePreviewImage(dispUrl || url);
        });
      });
    }
  }

  /**
   * 获取当前选中的图片 URL
   */
  getSelectedImageUrl() {
    return this.selectedImageUrl;
  }

  getSelectedJobId() {
    return this.selectedJobId;
  }

  /**
   * 获取当前选中的展示图片 URL (fileKeyImg1)
   */
  getSelectedDisplayUrl() {
    return this.selectedDisplayUrl;
  }

  /**
   * 重置到预览状态
   */
  resetToPreview() {
    const previewState = document.getElementById("uploadPreviewState");
    const cropState = document.getElementById("uploadCropState");
    const progressState = document.getElementById("uploadProgressState");
    const imageSelectionState = document.getElementById("imageSelectionState");
    const errorState = document.getElementById("uploadErrorState");
    const title = this.modal.querySelector(".global-upload-modal-title");
    this.currentState = "preview";

    // 停止轮播动画
    this.stopCarousel();
    this.setGenerateSubmitting(false);

    if (previewState) previewState.style.display = "block";
    if (cropState) cropState.style.display = "none";
    if (progressState) progressState.style.display = "none";
    if (imageSelectionState) imageSelectionState.style.display = "none";
    if (errorState) errorState.style.display = "none"; // 隐藏错误状态
    if (title) {
      title.textContent = "Preview Upload File";
      title.style.display = "block";
    }

    // Show the full-width Better Generate button in preview state.
    if (this.buttonsContainer) this.buttonsContainer.style.display = "flex";

    const isN8nTemplate = window.SNAPFIG_N8N_TEMPLATE === true;
    // Preserve the fridge magnet context previously refreshed by button visibility checks.
    this.detectIsBadge();
    const isFunko = !isN8nTemplate && this.detectIsFunko();

    if (this.betterButton) {
      this.betterButton.style.display = "inline-flex";
      const betterName = this.betterButton.querySelector(".btn-name");
      const betterTime = this.betterButton.querySelector(".btn-time");
      if (betterName) {
        betterName.textContent = this.getGenerateButtonText();
      }
      if (betterTime) betterTime.style.display = isFunko ? "none" : "";
    }

    if (this.confirmButton) {
      this.confirmButton.style.display = "none";
      this.confirmButton.disabled = false;
    }

    // Hide regenerate button in preview state
    if (this.regenerateButton) {
      this.regenerateButton.style.display = "none";
    }

    // Hide history state
    if (this.historyState) this.historyState.style.display = "none";

    // Hide promo notice in preview state
    const notice = this.modal.querySelector(".upload-info-notice");
    if (notice) notice.style.display = "none";

    // Show Recent Result button in preview state
    if (this.btnRecentResult) this.btnRecentResult.style.display = "flex";

    // 清除图片选择网格（清除之前失败或成功的图片状态）
    const imagesGrid = document.getElementById("select-image-list");
    if (imagesGrid) {
      imagesGrid.innerHTML = "";
    }

    // 清除大预览图
    const previewMain = document.getElementById("image-preview-main");
    if (previewMain) {
      previewMain.innerHTML = '<div class="image-loading-spinner"></div>';
      previewMain.setAttribute("data-url", "");
    }

    // 重置选中的图片URL
    this.selectedImageUrl = null;
    this.selectedDisplayUrl = null;
    this.selectedImageModelName = null;
  }

  /**
   * 显示错误信息
   * @param {string} title - 错误标题
   * @param {string} message - 错误消息
   * @param {string} details - 错误详情（可选）
   */
  showError(title, message, details) {
    // Filter out specific error messages that should not be displayed
    const suppressedErrors = [
      "Invalid parameters found: category",
    ];

    const fullMessage = `${message || ""} ${details || ""}`;
    if (suppressedErrors.some(err => fullMessage.includes(err))) {
      return;
    }

    // 取消正在进行的请求
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // 停止进度动画
    if (this.progressAnimation) {
      this.progressAnimation.stop();
      this.progressAnimation = null;
    }

    // 停止轮播动画
    this.stopCarousel();
    this.setGenerateSubmitting(false);

    // 隐藏其他状态
    const previewState = document.getElementById("uploadPreviewState");
    const progressState = document.getElementById("uploadProgressState");
    const imageSelectionState = document.getElementById("imageSelectionState");

    if (previewState) previewState.style.display = "none";
    if (progressState) progressState.style.display = "none";
    if (imageSelectionState) imageSelectionState.style.display = "none";

    // 创建或获取错误状态容器
    let errorState = document.getElementById("uploadErrorState");
    if (!errorState) {
      errorState = document.createElement("div");
      errorState.id = "uploadErrorState";
      errorState.className = "upload-error-state";
      const modalContent = this.modal.querySelector(
        ".global-upload-modal-content"
      );
      modalContent.appendChild(errorState);
    }

    // 设置错误内容
    errorState.innerHTML = `
      <div class="error-icon">⚠️</div>
      <h3 class="error-title">${title}</h3>
      <p class="error-message">${message}</p>
      ${details ? `<p class="error-details">${details}</p>` : ""}
    `;
    errorState.style.display = "block";

    // 修改标题和按钮
    const modalTitle = this.modal.querySelector(".global-upload-modal-title");
    if (modalTitle) modalTitle.textContent = "Upload Error";

    // Hide all buttons during error state
    if (this.buttonsContainer) this.buttonsContainer.style.display = "none";

    // Hide Recent Result button in error state
    if (this.btnRecentResult) this.btnRecentResult.style.display = "none";

    // Note: cancelButton no longer exists, user can use the X close button
  }

  /**
   * 启动轮播动画
   */
  startCarousel() {
    // 重置轮播索引
    this.currentCarouselIndex = 0;

    // 确保第一个轮播组是可见的
    this.updateCarousel();

    // 启动自动轮播（每4秒切换一次）
    this.carouselInterval = setInterval(() => {
      const carouselGroups = document.querySelectorAll(".carousel-group");
      this.currentCarouselIndex =
        (this.currentCarouselIndex + 1) % carouselGroups.length;
      this.updateCarousel();
    }, 4000);
  }

  /**
   * 停止轮播动画
   */
  stopCarousel() {
    if (this.carouselInterval) {
      clearInterval(this.carouselInterval);
      this.carouselInterval = null;
    }
  }

  /**
   * 更新轮播显示状态
   */
  /**
   * Show History State and fetch data
   */
  /**
   * Helper to create a canvas with watermarked image
   * @param {string} url - Image URL
   * @returns {Promise<HTMLCanvasElement>} - Promise that resolves with canvas element
   */

  /**
   * Show History State and fetch data
   */
  async showHistory() {
    const previewState = document.getElementById("uploadPreviewState");
    const imageSelectionState = document.getElementById("imageSelectionState");
    if (previewState) previewState.style.display = "none";
    if (imageSelectionState) imageSelectionState.style.display = "none";
    if (this.historyState) this.historyState.style.display = "block";

    const title = this.modal.querySelector(".global-upload-modal-title");
    if (title) title.textContent = "Recent Results";

    // Hide main buttons
    if (this.buttonsContainer) this.buttonsContainer.style.display = "none";

    // Hide Recent Result button when in history view
    if (this.btnRecentResult) this.btnRecentResult.style.display = "none";

    // Reset history state
    this.historyPage = 1;
    this.historyHasMore = true;
    this.historyLoading = false;
    this.historyItemCount = 0; // Reset item count
    this.selectedHistoryUrl = null; // Clear selection
    this.selectedHistoryDisplayUrl = null; // Clear display selection
    this.selectedHistoryModelName = null;
    if (this.btnConfirmHistory) {
      this.btnConfirmHistory.disabled = true;
      this.btnConfirmHistory.style.opacity = "0.5";
    }
    if (this.historyGrid) this.historyGrid.innerHTML = "";

    // Remove existing scroll listener to avoid duplicates
    if (this.historyScrollHandler && this.historyGrid) {
      this.historyGrid.removeEventListener("scroll", this.historyScrollHandler);
    }

    // Check current scroll position and load more if needed
    this.historyScrollHandler = () => {
      if (this.historyGrid) {
        const { scrollTop, scrollHeight, clientHeight } = this.historyGrid;
        if (scrollHeight - scrollTop <= clientHeight + 50) {
          // Threshold 50px
          this.loadMoreHistory();
        }
      }
    };

    if (this.historyGrid) {
      this.historyGrid.addEventListener("scroll", this.historyScrollHandler);
    }

    await this.fetchHistory();
  }

  /**
   * Back from history - return to image selection state or close modal
   */
  backFromHistory() {
    // If opened directly to history view, close the modal instead of going back
    if (this.openedDirectlyToHistory) {
      this.close();
      return;
    }

    const imageSelectionState = document.getElementById("imageSelectionState");
    if (this.historyState) this.historyState.style.display = "none";
    if (imageSelectionState) imageSelectionState.style.display = "block";

    const title = this.modal.querySelector(".global-upload-modal-title");
    if (title) title.textContent = "Select Your Design";

    // Show main buttons
    if (this.buttonsContainer) this.buttonsContainer.style.display = "flex";

    // Show Recent Result button again
    if (this.btnRecentResult) this.btnRecentResult.style.display = "flex";
  }

  /**
   * Confirm history selection - close modal with selected history image
   */
  confirmHistorySelection() {
    if (!this.selectedHistoryUrl) return;

    // Set the selected image URL for external access
    this.selectedImageUrl = this.selectedHistoryUrl;
    this.selectedDisplayUrl = this.selectedHistoryDisplayUrl || "";
    this.selectedJobId = this.selectedHistoryJobId || "";
    this.isFromHistory = true;

    // Track analytics if available
    if (typeof GtagUtils !== "undefined") {
      GtagUtils.trackDesignConfirm(this.selectedHistoryJobId, {
        generation_model: this.selectedHistoryModelName
          ? GtagUtils.getModelName(this.selectedHistoryModelName)
          : this.selectedImageModelName || this.getCurrentModelName(),
      });
    }

    // Call callback with null file to indicate history selection
    if (this.confirmCallback) {
      this.confirmCallback(null, null);
    }

    this.close();
  }

  async loadMoreHistory() {
    if (this.historyLoading || !this.historyHasMore) return;
    // Check if we've reached the maximum limit
    if (this.historyItemCount >= this.maxHistoryItems) {
      this.historyHasMore = false;
      return;
    }
    this.historyPage++;
    await this.fetchHistory(true);
  }

  /**
   * Detect character type based on current product (same logic as image-uploader.js)
   * @returns {Promise<string>} 'pet', 'person', 'couple', or 'human-pet'
   */
  async detectCharacterType() {
    let characterType = "person"; // 默认值

    // Reset fridge magnet flag
    this._isFridgeMagnet = false;

    // Try to find the active container (drawer, popover, or main page)
    let container = document.querySelector("quick-buy-drawer[open]");
    if (!container) {
      container = document.querySelector("quick-buy-popover[open]");
    }
    if (!container) {
      container = document.querySelector(".main-product") || document.body;
    }

    // Get product title
    let titleText = "";
    let productHandle = "";

    // Method 1: Get product handle from form and fetch product data
    const productForm = container.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const formAction = container.baseURI;
      const productHandleMatch = formAction?.match(/products\/([^\/\?]+)/);

      if (productHandleMatch) {
        productHandle = productHandleMatch[1];
        try {
          // Check for product data script tag
          const productDataScript = container.querySelector(
            'script[type="application/json"][data-product-json]'
          );
          if (productDataScript) {
            const productData = JSON.parse(productDataScript.textContent);
            titleText = productData.title;
          } else {
            const response = await fetch(`/products/${productHandle}.js`);
            if (response.ok) {
              const productData = await response.json();
              titleText = productData.title;
            }
          }
        } catch (e) {}
      }
    }

    // Method 2: Find title element in container (fallback)
    if (!titleText) {
      const titleSelectors = [
        ".product-item-meta__title",
        ".product__title",
        ".product-meta__title",
        "h1",
        '[class*="title"]',
      ];

      for (const selector of titleSelectors) {
        const productTitle = container.querySelector(selector);
        if (productTitle && productTitle.textContent.trim()) {
          titleText = productTitle.textContent.trim();
          break;
        }
      }
    }

    // Method 3: Use Shopify global object (product page only)
    if (
      !titleText &&
      typeof window.ShopifyAnalytics !== "undefined" &&
      window.ShopifyAnalytics.meta &&
      window.ShopifyAnalytics.meta.product
    ) {
      titleText = window.ShopifyAnalytics.meta.product.name || "";
    }

    // Check title for "1 person + 1 pet"
    const hasHumanPetInTitle = /1\s*person\s*\+\s*1\s*pet/i.test(titleText);
    let hasPetInTitle = /\bpet\b/i.test(titleText);

    // Check selected variant options
    const allSelectedOptions = container.querySelectorAll(
      'input[type="radio"][name^="option"]:checked'
    );
    let hasPetInOption = false;
    let hasCoupleInOption = false;
    let hasHumanPetInOption = false;
    let hasPersonInOption = false;
    let hasSingleInOption = false; // Check for "Single" option
    let hasMultipleInOption = false; // Check for "Multiple" option

    allSelectedOptions.forEach((radio) => {
      const optionValue = radio.value || "";
      const optionText = radio.getAttribute("data-option-value") || optionValue;

      // Check for "Fridge magnet" - if so, force return person
      if (/fridge\s*magnet/i.test(optionText)) {
        characterType = "person";
        // Set flag to indicate this is a fridge magnet product (treated like isBadge)
        this._isFridgeMagnet = true;
      }

      // Check for "Single" - return person
      if (/\bsingle\b/i.test(optionText)) {
        hasSingleInOption = true;
      }

      // Check for "Multiple" - return group
      if (/\bmultiple\b/i.test(optionText)) {
        hasMultipleInOption = true;
      }

      // Check for "1 person + 1 pet"
      if (/1\s*person\s*\+\s*1\s*pet/i.test(optionText)) {
        hasHumanPetInOption = true;
      }

      // Check for "1 person", "2 people" etc.
      if (
        /\d+\s*person/i.test(optionText) ||
        /\d+\s*people/i.test(optionText)
      ) {
        hasPersonInOption = true;
      }

      // Check for "pet" or "Pet"
      if (/\bpet\b/i.test(optionText)) {
        hasPetInOption = true;
      }

      // Check for "2 people (connected)" or "couple" or "Couple"
      if (
        /2\s*people\s*\(\s*connected\s*\)/i.test(optionText) ||
        /2\s*people\s*\(connected\)/i.test(optionText) ||
        /2\s*people\s*\(\s*together\s*\)/i.test(optionText) ||
        /\bcouple\b/i.test(optionText)
      ) {
        hasCoupleInOption = true;
      }
    });

    // If Fridge magnet is detected, return person immediately (highest priority)
    if (this._isFridgeMagnet) {
      return "person";
    }

    // If "Multiple" option is selected, return group
    if (hasMultipleInOption) {
      return "group";
    }

    // If person options exist, force hasPetInTitle to false
    if (hasPersonInOption) {
      hasPetInTitle = false;
    }

    // Combine pet detection from title and options
    hasPetInOption = hasPetInOption || hasPetInTitle;

    // Return character type based on priority
    if (hasHumanPetInTitle || hasHumanPetInOption) {
      return "human-pet";
    }

    if (hasPetInOption) {
      return "pet";
    }

    if (hasCoupleInOption) {
      return "couple";
    }

    // If "Single" option is selected, return person
    if (hasSingleInOption) {
      return "person";
    }

    return characterType; // Default: "person"
  }

  /**
   * Detect current style from product options
   * @returns {string} Style value (Q-Style, Pixar-Style, Minecraft-Style, Funko-Style, Realistic-Style)
   */
  detectCurrentStyle() {
    // Try to find the active container (drawer, popover, or main page)
    let container = document.querySelector("quick-buy-drawer[open]");
    if (!container) {
      container = document.querySelector("quick-buy-popover[open]");
    }
    if (!container) {
      container = document.querySelector(".main-product") || document.body;
    }

    // Method 1: Get from global variable (priority)
    const selectedOptions = window.selectedProductOptions;
    if (selectedOptions && selectedOptions.style) {
      const mappedStyle = this.styleMapping[selectedOptions.style];
      if (mappedStyle) {
        return mappedStyle;
      }
    }

    // Method 2: Find all checked radio buttons in container
    const allCheckedRadios = container.querySelectorAll(
      'input[type="radio"][name^="option"]:checked'
    );

    // Smart find style option: check if value is in mapping
    for (const radio of allCheckedRadios) {
      const value = radio.value;
      const mappedStyle = this.styleMapping[value];
      if (mappedStyle) {
        return mappedStyle;
      }
    }

    // Method 3: Get from display value in container (fallback)
    const allValueDisplays = container.querySelectorAll(
      '[id*="option"][id*="-value"]'
    );
    for (const display of allValueDisplays) {
      const value = display.textContent.trim();
      const mappedStyle = this.styleMapping[value];
      if (mappedStyle) {
        return mappedStyle;
      }
    }

    // Method 4: Get from option selectors in container (final fallback)
    const allOptionSelectors = container.querySelectorAll(
      ".product-form__option-selector"
    );
    for (let index = 0; index < allOptionSelectors.length; index++) {
      const selector = allOptionSelectors[index];
      const checkedInput = selector.querySelector("input:checked");

      if (checkedInput) {
        const mappedStyle = this.styleMapping[checkedInput.value];
        if (mappedStyle) {
          return mappedStyle;
        }
      }
    }

    return "Q-Style"; // Default style
  }

  /**
   * Detect if current product is a Badge product (or Fridge magnet)
   * @returns {boolean} Whether this is a Badge product or Fridge magnet
   */
  detectIsBadge() {
    // Check if Fridge magnet was detected (set by detectCharacterType)
    if (this._isFridgeMagnet) {
      return true;
    }

    // Get current active container
    let container = document.querySelector("quick-buy-drawer[open]");
    if (!container) {
      container = document.querySelector("quick-buy-popover[open]");
    }
    if (!container) {
      container = document.querySelector(".main-product") || document.body;
    }

    // Check for "Fridge magnet" option directly (in case detectCharacterType wasn't called first)
    const allSelectedOptions = container.querySelectorAll(
      'input[type="radio"][name^="option"]:checked'
    );
    for (const radio of allSelectedOptions) {
      const optionValue = radio.value || "";
      const optionText = radio.getAttribute("data-option-value") || optionValue;
      if (/fridge\s*magnet/i.test(optionText)) {
        this._isFridgeMagnet = true;
        return true;
      }
    }

    // Read isBadge from product metafields JSON
    const metafieldsScript = container.querySelector('.variant-metafields-data');
    if (metafieldsScript) {
      try {
        const data = JSON.parse(metafieldsScript.textContent);
        const productMetafields = data.productMetafields || {};

        const isBadge = productMetafields.isBadge;
        if (isBadge === 'yes' || isBadge === 'YES' || isBadge === 'Yes' || isBadge === true) {
          return true;
        }
      } catch (e) {}
    }

    return false;
  }


  
  /**
   * Detect if current product is a Phone case product
   * @returns {boolean} Whether this is a Phone case product
   */
  detectIsPhone() {
    // Get current active container
    let container = document.querySelector("quick-buy-drawer[open]");
    if (!container) {
      container = document.querySelector("quick-buy-popover[open]");
    }
    if (!container) {
      container = document.querySelector(".main-product") || document.body;
    }

    // Read isPhone from product metafields JSON
    const metafieldsScript = container.querySelector('.variant-metafields-data');
    if (metafieldsScript) {
      try {
        const data = JSON.parse(metafieldsScript.textContent);
        const productMetafields = data.productMetafields || {};

        const isPhone = productMetafields.isPhone;
        if (isPhone === 'yes' || isPhone === 'YES' || isPhone === 'Yes' || isPhone === true) {
          return true;
        }
      } catch (e) {}
    }

    return false;
  }

  /**
   * Detect if current product is a face swapping doll product
   * @returns {boolean} Whether this is a face swapping doll product
   */
  detectIsFaceSwappingDoll() {
    let container = document.querySelector("quick-buy-drawer[open]");
    if (!container) {
      container = document.querySelector("quick-buy-popover[open]");
    }
    if (!container) {
      container = document.querySelector(".main-product") || document.body;
    }

    const metafieldsScript = container.querySelector('.variant-metafields-data');
    if (metafieldsScript) {
      try {
        const data = JSON.parse(metafieldsScript.textContent);
        const productMetafields = data.productMetafields || {};

        const isFaceSwappingDoll = productMetafields.isFaceSwappingDoll;
        if (isFaceSwappingDoll === 'yes' || isFaceSwappingDoll === 'YES' || isFaceSwappingDoll === 'Yes' || isFaceSwappingDoll === true) {
          return true;
        }
      } catch (e) {}
    }

    return false;
  }

  /**
   * Detect if current product is a Funko product
   * @returns {boolean} Whether this is a Funko product
   */
  detectIsFunko() {
    let container = document.querySelector("quick-buy-drawer[open]");
    if (!container) {
      container = document.querySelector("quick-buy-popover[open]");
    }
    if (!container) {
      container = document.querySelector(".main-product") || document.body;
    }

    const metafieldsScript = container.querySelector('.variant-metafields-data');
    if (metafieldsScript) {
      try {
        const data = JSON.parse(metafieldsScript.textContent);
        const productMetafields = data.productMetafields || {};

        const isFunko = productMetafields.isFunko;
        if (isFunko === 'yes' || isFunko === 'YES' || isFunko === 'Yes' || isFunko === true) {
          return true;
        }
      } catch (e) {}
    }

    return false;
  }

  /**
   * Detect if current product is an Aroma Stone product
   * @returns {boolean} Whether this is an Aroma Stone product
   */
  detectIsAromaStone() {
    let container = document.querySelector("quick-buy-drawer[open]");
    if (!container) {
      container = document.querySelector("quick-buy-popover[open]");
    }
    if (!container) {
      container = document.querySelector(".main-product") || document.body;
    }

    const metafieldsScript = container.querySelector('.variant-metafields-data');
    if (metafieldsScript) {
      try {
        const data = JSON.parse(metafieldsScript.textContent);
        const productMetafields = data.productMetafields || {};

        const aromaStone = productMetafields.aromaStone;
        if (aromaStone === 'yes' || aromaStone === 'YES' || aromaStone === 'Yes' || aromaStone === true) {
          return true;
        }
      } catch (e) {}
    }

    return false;
  }

  /**
   * Detect if current product is a Keychain product
   * @returns {boolean} Whether this is a Keychain product
   */
  // detectIsKeychain() {
  //   // Get current active container
  //   let container = document.querySelector("quick-buy-drawer[open]");
  //   if (!container) {
  //     container = document.querySelector("quick-buy-popover[open]");
  //   }
  //   if (!container) {
  //     container = document.querySelector(".main-product") || document.body;
  //   }

  //   // Read isKeychain from product metafields JSON
  //   const metafieldsScript = container.querySelector('.variant-metafields-data');
  //   if (metafieldsScript) {
  //     try {
  //       const data = JSON.parse(metafieldsScript.textContent);
  //       const productMetafields = data.productMetafields || {};

  //       const isKeychain = productMetafields.iskeychain;
  //       if (isKeychain === 'yes' || isKeychain === 'YES' || isKeychain === 'Yes' || isKeychain === true) {
  //         return true;
  //       }
  //     } catch (e) {}
  //   }

  //   return false;
  // }

  /**
   * Get current selected variant ID
   * @returns {string|null}
   */
  getCurrentVariantId() {
    let variantId = window.currentSelectedVariant?.id || null;

    if (!variantId) {
      let container = document.querySelector("quick-buy-drawer[open]");
      if (!container) {
        container = document.querySelector("quick-buy-popover[open]");
      }
      if (!container) {
        container = document.querySelector(".main-product") || document.body;
      }

      const scopedVariantInput =
        container.querySelector('input[name="id"]') ||
        container.querySelector('select[name="id"]');

      if (scopedVariantInput && scopedVariantInput.value) {
        variantId = scopedVariantInput.value;
      }

      if (!variantId) {
        const allSelects = document.querySelectorAll('select[name="id"]');
        const allInputs = document.querySelectorAll('input[name="id"]');

        if (allSelects.length > 0) {
          variantId = allSelects[allSelects.length - 1].value;
        }
        if (!variantId && allInputs.length > 0) {
          variantId = allInputs[allInputs.length - 1].value;
        }
      }
    }

    return variantId;
  }

  /**
   * Fetch history from API
   * @param {boolean} isLoadMore - Whether this is a load more request
   */
  async fetchHistory(isLoadMore = false) {
    if (!this.historyGrid) return;
    this.historyLoading = true;
    let shouldFillHistory = false;

    // Show loading indicator only if initial load
    if (!isLoadMore) {
      this.historyGrid.innerHTML =
        '<div class="history-loading" style="grid-column: 1 / -1; text-align: center; padding: 20px;">Loading...</div>';
    } else {
      // Show loading indicator at the bottom for load more
      const loadingEl = document.createElement("div");
      loadingEl.className = "history-loading-more";
      loadingEl.style.cssText =
        "grid-column: 1 / -1; text-align: center; padding: 15px;";
      loadingEl.innerHTML = '<div class="history-loading-spinner"></div>';
      this.historyGrid.appendChild(loadingEl);
    }

    try {
      const isN8nTemplate = window.SNAPFIG_N8N_TEMPLATE === true;
      const baseUrl = isN8nTemplate
        ? window.THEME_CONFIG?.n8nApiBaseUrl || "https://alb.neural4d.com:3000"
        : window.THEME_CONFIG?.apiBaseUrl || "https://alb.deliver3d.net:3000";
      const token =
        localStorage.getItem("deliver3d_guest_token") || window.guestToken;

      if (!token) {
        throw new Error("Please upload an image first to establish a session.");
      }

      // Detect character type and style dynamically
      let characterType = await this.detectCharacterType();
      let style = this.detectCurrentStyle();
      const isBadge = this.detectIsBadge();
      const isPhone = this.detectIsPhone();
      const isFaceSwappingDoll = this.detectIsFaceSwappingDoll();
      const isAromaStone = this.detectIsAromaStone();
      // const isKeychain = this.detectIsKeychain();
      if (isBadge && (characterType === "pet" || characterType === "human-pet")) {
        characterType = "group";
      }

      // Phone case 产品使用不同的风格映射
      if (isPhone) {
        const phoneCaseStyleMap = {
          'Q-Style': 'chibi',
          'Pixar-Style': 'pixar',
          'Funko-Style': 'funko'
        };
        style = phoneCaseStyleMap[style] || style;
      }

      if (isFaceSwappingDoll) {
        style = "Face-Swap-Doll";
      }

      if (isAromaStone) {
        style = "Aroma-Stone";
      }

      // n8n history is filtered by the currently selected Shopify variant.
      const currentVariantId = this.getCurrentVariantId() || "";
      let historyUrl = isN8nTemplate
        ? `${baseUrl}/n8n/jobHistory?page=${this.historyPage}&pageSize=5&varientId=${encodeURIComponent(currentVariantId)}`
        : `${baseUrl}/teamCuteTasks/getTaskHistory?page=${this.historyPage}&pageSize=5&taskType=all&characterType=${characterType}&styleType=${encodeURIComponent(style)}`;
      if (isBadge) {
        historyUrl += `&category=portrait`;
        if (!isN8nTemplate && currentVariantId) {
          historyUrl += `&variantId=${encodeURIComponent(currentVariantId)}`;
        }
      } else if (isPhone) {
        historyUrl += `&category=phone_case`;
      }

      // if (isKeychain) {
      //   historyUrl += `&keyChain=true`;
      // }

      const response = await fetch(
        historyUrl,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch history");

      const result = await response.json();
      if (result.success && result.data) {
        const newImages = [];
        result.data.forEach((task) => {
          if (isN8nTemplate && task?.source === 'n8n') {
            const output = task.output && typeof task.output === 'object' ? task.output : {};
            (Array.isArray(output.outputImages) ? output.outputImages : []).forEach((url) => {
              if (url) {
                newImages.push({
                  url,
                  displayUrl: url,
                  jobId: String(task.id || ''),
                  modelName: 'n8n'
                });
              }
            });
            return;
          }
          if (task.batches) {
            task.batches.forEach((batch) => {
              if (
                (batch.status === "completed" || batch.status === "complete") &&
                batch.batchData?.fileKeyImg0
              ) {
                newImages.push({
                  url: batch.batchData.fileKeyImg0,
                  displayUrl: batch.batchData.fileKeyImg1 || "",
                  jobId: batch.jobId,
                  modelName: task.modelName || task.model_name || task.sourceType || task.source_type || "",
                });
              }
            });
          }
        });

        // Calculate how many items we can still add
        const remainingSlots = this.maxHistoryItems - this.historyItemCount;
        const itemsToAdd = newImages.slice(0, remainingSlots);

        const currentPage = Number(result.pagination?.currentPage);
        const totalPages = Number(result.pagination?.totalPages);
        const hasValidPagination =
          Number.isFinite(currentPage) &&
          Number.isFinite(totalPages) &&
          totalPages > 0;

        this.historyHasMore = hasValidPagination
          ? currentPage < totalPages
          : result.data.length >= 5;

        this.renderHistory(itemsToAdd, isLoadMore);
        this.historyItemCount += itemsToAdd.length;
        if (this.historyItemCount >= this.maxHistoryItems) {
          this.historyHasMore = false;
        }
        shouldFillHistory = this.historyHasMore;
      } else {
        if (!isLoadMore) {
          this.historyGrid.innerHTML =
            '<div style="grid-column: 1 / -1; text-align: center;">No history found.</div>';
        }
        this.historyHasMore = false;
      }
    } catch (err) {
      if (!isLoadMore) {
        this.historyGrid.innerHTML = `<div style="grid-column: 1 / -1; text-align: center; color: red;">Error: ${err.message}</div>`;
      }
    } finally {
      this.historyLoading = false;
      // Remove loading indicators if they exist
      const loading = this.historyGrid.querySelector(".history-loading");
      if (loading) loading.remove();
      const loadingMore = this.historyGrid.querySelector(
        ".history-loading-more"
      );
      if (loadingMore) loadingMore.remove();

      if (
        shouldFillHistory &&
        this.historyGrid.scrollHeight <= this.historyGrid.clientHeight + 1
      ) {
        await this.loadMoreHistory();
      }
    }
  }

  /**
   * Render history images
   * @param {Array} images
   * @param {boolean} isLoadMore
   */
  renderHistory(images, isLoadMore) {
    if (!this.historyGrid) return;

    // If initial load, clear container (but we might have cleared it in fetchHistory start)
    // Actually fetchHistory sets loading text, so we should clear that if we have images
    if (!isLoadMore) {
      this.historyGrid.innerHTML = "";
      if (images.length === 0) {
        this.historyGrid.innerHTML =
          '<div class="history-empty" style="grid-column: 1 / -1; text-align: center;">No recent images found.</div>';
        return;
      }
    }

    if (images.length > 0) {
      const emptyState = this.historyGrid.querySelector(".history-empty");
      if (emptyState) emptyState.remove();
    }

    images.forEach((img) => {
      const itemDiv = document.createElement("div");
      itemDiv.className = "history-item";
      itemDiv.setAttribute("data-url", img.url);
      itemDiv.setAttribute("data-display-url", img.displayUrl || "");
      itemDiv.setAttribute("data-job-id", img.jobId || "");
      itemDiv.setAttribute("data-model-name", img.modelName || "");

      itemDiv.style.position = "relative";
      itemDiv.style.overflow = "hidden";

      // Load image - prefer displayUrl (fileKeyImg1) for display, fallback to url (fileKeyImg0)
      const imgEl = new Image();
      imgEl.style.width = "100%";
      imgEl.style.height = "100%";
      imgEl.style.objectFit = "contain";
      imgEl.oncontextmenu = () => false;

      imgEl.onload = () => {
        // Use CSS-only overlay (robust positioning) - disabled
        // const overlay = this.createWatermarkOverlay(false, true);
        // itemDiv.appendChild(overlay);
      };

      imgEl.src = img.displayUrl || img.url;
      itemDiv.appendChild(imgEl);

      itemDiv.addEventListener("click", () => {
        // Remove selected class from all history items
        const allItems = this.historyGrid.querySelectorAll(".history-item");
        allItems.forEach((item) => item.classList.remove("selected"));

        // Add selected class to clicked item
        itemDiv.classList.add("selected");

        // Store selected URL
        this.selectedHistoryUrl = itemDiv.getAttribute("data-url");
        this.selectedHistoryDisplayUrl = itemDiv.getAttribute("data-display-url") || "";
        this.selectedHistoryJobId = itemDiv.getAttribute("data-job-id") || "";
        this.selectedHistoryModelName = itemDiv.getAttribute("data-model-name") || "";

        // Enable confirm button
        if (this.btnConfirmHistory) {
          this.btnConfirmHistory.disabled = false;
          this.btnConfirmHistory.style.opacity = "1";
        }
      });

      this.historyGrid.appendChild(itemDiv);
    });
  }

  updateCarousel() {
    const carouselGroups = document.querySelectorAll(".carousel-group");

    carouselGroups.forEach((group, index) => {
      if (index === this.currentCarouselIndex) {
        group.classList.add("active");

        // 重新触发动画
        const leftCards = group.querySelectorAll(".animate-slide-in-left");
        const rightCards = group.querySelectorAll(".animate-slide-in-right");

        // 移除动画类
        leftCards.forEach((card) => {
          card.classList.remove("animate-slide-in-left");
        });
        rightCards.forEach((card) => {
          card.classList.remove("animate-slide-in-right");
        });

        // 强制重排以重新触发动画
        void group.offsetWidth;

        // 重新添加动画类
        leftCards.forEach((card) => {
          card.classList.add("animate-slide-in-left");
        });
        rightCards.forEach((card) => {
          card.classList.add("animate-slide-in-right");
        });
      } else {
        group.classList.remove("active");
      }
    });
  }
}

// 初始化全局上传弹窗
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.globalUploadModal = new GlobalUploadModal();
    setupRecentHistoryButtonDelegation();
    setupSafetyMechanism();
  });
} else {
  // DOM 已经加载完成，直接初始化
  window.globalUploadModal = new GlobalUploadModal();
  setupRecentHistoryButtonDelegation();
  setupSafetyMechanism();
}

/**
 * Handles Recent history buttons rendered into product forms after quick-buy
 * drawer/popover HTML is fetched. Inline scripts in fetched HTML do not reliably
 * execute, so this global delegate owns the click path.
 */
function setupRecentHistoryButtonDelegation() {
  if (window._globalUploadHistoryDelegationAttached) return;
  window._globalUploadHistoryDelegationAttached = true;

  document.addEventListener(
    "click",
    (event) => {
      const historyBtn = event.target.closest(
        "#ai-upload-history-btn, .ai-upload-section__history-btn"
      );
      if (!historyBtn) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const modal = window.globalUploadModal;
      if (!modal) return;

      modal.openedDirectlyToHistory = true;

      const clickedContainer =
        historyBtn.closest(
          "quick-buy-drawer, quick-buy-popover, .drawer__content, .popover__content, .product-form"
        ) || document;

      modal.confirmCallback = function () {
        const selectedUrl = modal.getSelectedImageUrl();
        const selectedDisplayUrl = modal.getSelectedDisplayUrl();
        const selectedJobId = modal.getSelectedJobId();

        if (selectedUrl) {
          const firstUploadInput = clickedContainer.querySelector(
            "#ai-custom-image-url"
          );
          const firstUploadHasImage =
            firstUploadInput && firstUploadInput.value.trim() !== "";
          const secondUploadContainer = clickedContainer.querySelector(
            '.ai-upload-container[data-upload-index="2"]'
          );
          const isSecondUploadVisible =
            secondUploadContainer &&
            window.getComputedStyle(secondUploadContainer).display !== "none";
          const uploadIndex =
            firstUploadHasImage && isSecondUploadVisible ? 2 : 1;
          const suffix = uploadIndex === 1 ? "" : "-2";

          const previewImg = clickedContainer.querySelector(
            `#ai-upload-preview${suffix}`
          );
          const uploadBtn = clickedContainer.querySelector(
            `#ai-upload-trigger${suffix}`
          );
          const hiddenInput = clickedContainer.querySelector(
            `#ai-custom-image-url${suffix}`
          );
          const displayInput = clickedContainer.querySelector(
            `#ai-custom-image-url${suffix}-display`
          );
          const jobIdInput = clickedContainer.querySelector(
            `#ai-custom-image-url${suffix}-job-id`
          );
          const statusDiv = clickedContainer.querySelector(
            `#ai-upload-status${suffix}`
          );

          if (previewImg && uploadBtn) {
            previewImg.src = selectedDisplayUrl || selectedUrl;
            uploadBtn.classList.add("has-image");
            uploadBtn.classList.remove("validation-error");
          }

          if (hiddenInput) {
            hiddenInput.value = selectedUrl;
            hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
          }

          if (displayInput) {
            displayInput.value = selectedDisplayUrl || selectedUrl;
            displayInput.dispatchEvent(new Event("change", { bubbles: true }));
          }

          if (jobIdInput) {
            const jobIdValue = String(selectedJobId || "").trim();
            jobIdInput.value =
              window.SNAPFIG_N8N_TEMPLATE === true && jobIdValue
                ? `n8n${jobIdValue}`
                : jobIdValue;
            jobIdInput.dispatchEvent(new Event("change", { bubbles: true }));
          }

          if (statusDiv) {
            statusDiv.textContent = "✓ Design selected from history!";
            statusDiv.className = "ai-upload-status success";
          }

          if (
            window.SNAPFIG_N8N_TEMPLATE === true &&
            typeof window.openN8nUpsellWhenReady === "function"
          ) {
            window.openN8nUpsellWhenReady();
          }
        }

        modal.openedDirectlyToHistory = false;
      };

      modal.open();
      modal.showHistory();
    },
    true
  );
}

/**
 * 安全机制：防止抽屉永久锁定
 * 当上传弹窗关闭但抽屉仍被锁定时，自动解锁
 */
function setupSafetyMechanism() {
  // 检测弹窗是否可见
  function isModalVisible() {
    const modal = document.getElementById("globalUploadModal");
    return modal && modal.classList.contains("active");
  }

  // 解锁所有抽屉的函数
  function unlockAllDrawers() {
    const allDrawers = document.querySelectorAll("quick-buy-drawer");
    const allPopovers = document.querySelectorAll("quick-buy-popover");
    const cartDrawers = document.querySelectorAll("cart-drawer");

    let unlocked = false;

    allDrawers.forEach((drawer) => {
      if (
        drawer.dataset.modalOpen === "true" ||
        drawer.dataset.uploadInProgress === "true"
      ) {
        drawer.dataset.modalOpen = "false";
        drawer.dataset.uploadInProgress = "false";
        unlocked = true;
      }
    });
    allPopovers.forEach((popover) => {
      if (
        popover.dataset.modalOpen === "true" ||
        popover.dataset.uploadInProgress === "true"
      ) {
        popover.dataset.modalOpen = "false";
        popover.dataset.uploadInProgress = "false";
        unlocked = true;
      }
    });
    cartDrawers.forEach((drawer) => {
      if (
        drawer.dataset.modalOpen === "true" ||
        drawer.dataset.uploadInProgress === "true"
      ) {
        drawer.dataset.modalOpen = "false";
        drawer.dataset.uploadInProgress = "false";
        unlocked = true;
      }
    });

  }

  // 定期检查：如果弹窗不可见但抽屉被锁定，自动解锁
  setInterval(() => {
    if (!isModalVisible()) {
      unlockAllDrawers();
    }
  }, 2000); // 每2秒检查一次

  // 监听页面可见性变化
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && !isModalVisible()) {
      unlockAllDrawers();
    }
  });

  // 监听 ESC 键（用户可能按 ESC 关闭弹窗）
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !isModalVisible()) {
      unlockAllDrawers();
    }
  });
}
